/**
 * Owner Audit screen (design §5, §8.4). Owner nav only.
 * Failed actions go through the one failure home (ui/failure.ts).
 * Hub and bank text is untrusted: text nodes only.
 */
import { amVisibleDisplayText } from "../../../ingest-worker/src/am_visible_text.js";
import { AdminApiError, type AdminClient } from "../api.js";
import { mountChartChooser } from "../chart_chooser.js";
import { clear, h, safeText } from "../dom.js";
import { safeReceiptImageHref } from "../receipt_image.js";
import { resolveScopeMembers, type HomeScope } from "../scope.js";
import type {
  AccountPath,
  ChartSnapshot,
  Company,
  CompanySlug,
  DashboardGroup,
  ExpenseAuditDecisionBody,
  ExpenseAuditDecisionView,
  ExpenseAuditItem,
  ExpenseAuditLeg,
  ExpenseAuditViewRecord,
  StaffRole,
} from "../types.js";
import { advice } from "../ui/capabilities.js";
import { auditAccountAllowed } from "../ui/audit-accounts.js";
import * as face from "../ui/face.js";
import {
  mountChangeCategory,
  MULTI_LINE_CHANGE_TEXT as SPLIT_ONLY_TEXT,
  NO_BOOKING_LINES_TEXT,
} from "../ui/change-category.js";
import { failureCopy, outcomeSentence } from "../ui/failure.js";
import { readerRole } from "../ui/facts.js";
import { navLabel, sentence } from "../ui/sentences.js";
import { runWrite } from "../ui/write.js";
import {
  accountLeafLabel,
  companyAccountLabels,
  countOrMoreText,
  dateText,
  moneyText,
  monthText,
  type AccountLabelContext,
} from "../ui/display-words.js";
import { decimalToCents } from "../ui/money.js";
import {
  centsToAmount,
  mountSplitEditor,
  resolvedSplitCents,
  type SplitLineDraft,
} from "../ui/split-editor.js";

export const IC_SPLIT_COMPANIES: readonly string[] = [
  "gv33",
  "gvkw",
  "beechgrove",
  "square127",
  "crestwood",
  "sor-properties",
  "partners141",
];

/** The Worker's IC split set, for lookups (a slug is a key here, not words). */
const IC_SPLIT_SET: ReadonlySet<string> = new Set(IC_SPLIT_COMPANIES);

export const DEFAULT_MAINTENANCE_SUFFIXES: readonly string[] = [
  "Repairs",
  "Remodel",
  "Turnover",
];

export const DEFAULT_QUICK_PICKS: readonly { name: string; suffix: string }[] = [
  { name: "Repairs", suffix: "Repairs" },
  { name: "Remodeling", suffix: "Capital-Improvements:Remodeling" },
  { name: "Turnover", suffix: "Turnover" },
  { name: "Appliances", suffix: "Appliances" },
  { name: "HVAC", suffix: "HVAC" },
  { name: "Plumbing", suffix: "Plumbing" },
  { name: "Electrical", suffix: "Electrical" },
  { name: "Flooring", suffix: "Flooring" },
  { name: "New HVAC", suffix: "Capital-Improvements:New-HVAC" },
];

const FILTER_KEY = "gv-books.expense-audit.filters";
const STRIP_KEY = "gv-books.expense-audit.hide-key-strip";
const VIEW_KEY = "gv-books.expense-audit.view-details";
const LOCAL_MAINTENANCE_ID = "__maintenance__";
/** The built-in group's name, and the company's own group of the same name. */
const MAINTENANCE_NAME = "maintenance";

/** The visible owner-only sentence the disabled account-manager controls point at. */
export const OWNER_ONLY_NOTE_ID = "audit-owner-only-note";

/** Shown in the live region when a write key arrives before the item could be read. */
export const WAIT_FOR_ITEM_TEXT = "Wait: new item";

/**
 * R5-02/R5-05: a quick pick or a Category change rewrites the WHOLE item as one
 * line. That is only the same booking when the item IS one line: on a split (or
 * a paired intercompany item, whose stored legs include the Due-From line) it
 * would move part of the money to the first leg's place without saying so.
 *
 * The words live in `ui/change-category.ts` beside the one control that reads
 * them, so this page and the transaction detail cannot say it two ways.
 */
export {
  MULTI_LINE_CHANGE_TEXT as SPLIT_ONLY_TEXT,
  NO_BOOKING_LINES_TEXT,
} from "../ui/change-category.js";

/**
 * Round-11 decision 5's contract change is deferred: a Worker-originated paired
 * split after a reload cannot be opened honestly (the payload carries only the
 * primary row and no company). The editor says so instead of inventing lines.
 * (Orchestrator design call, E-B11; exact wording.)
 */
export const PAIRED_SPLIT_TEXT = "This two-company split can't be opened here yet.";

/** Enter while this item's own write is still posting. Mirrors Undo's sentence. */
export const LOOKS_RIGHT_SAVING_TEXT =
  "Still saving this item. Looks right when the save has finished.";

/** Enter while a decision for this item is waiting for the next update. */
export const LOOKS_RIGHT_PENDING_TEXT =
  "This item has a change waiting for the next update. Looks right after that.";

/** Enter while this item is open for a Fix. */
export const LOOKS_RIGHT_FIX_OPEN_TEXT = "This item is open for a fix. Press Escape to close it.";

/** Undo when the Worker says the decision already applied (409 cannot pass). */
export const ALREADY_APPLIED_TEXT =
  "This change is already in the books. Use Fix to change the category.";

/**
 * The quick-pick sentence: it belongs to a chart that LOADED and has no
 * matching account. A chart that failed or has not arrived says nothing here —
 * the page's own failed-chart message is the only message, and "Use Category"
 * would send the person to a field that cannot work either (round nine B9-H3).
 * A chart that loaded with nothing bookable cannot offer Category to use, so it
 * says so instead of pointing at an empty field (round ten 4(e)).
 */
export function quickPickNoteText(
  chartLoaded: boolean,
  availableCount: number,
  bookableCount: number,
): string | null {
  if (!chartLoaded) return null;
  if (availableCount > 0) return null;
  if (bookableCount === 0) return "This company has no accounts to pick from yet.";
  return "No quick picks match this company. Use Category.";
}

export interface ExpenseAuditPageOptions {
  client: AdminClient;
  root: HTMLElement;
  company: CompanySlug;
  companies: readonly Company[];
  scope: HomeScope;
  groups: readonly DashboardGroup[];
  companyLabels: ReadonlyMap<string, string>;
  /** The shell's ledger segment to company name read (rd13 L1, review round 2). */
  companySegments?: ReadonlyMap<string, string>;
  isCurrent?: () => boolean;
  /** Signed-in role. Omitted, the shared reader decides (owner decision 10). */
  role?: StaffRole;
}

/** A company selector value: one company, the whole group, or every company. */
type CompanyMode = CompanySlug | "group" | "all";

interface LoadedItem {
  company: CompanySlug;
  item: ExpenseAuditItem;
}

interface DoneCard {
  loaded: LoadedItem;
  changeLabel: string;
  decisionId: string | null;
  kind: "review" | "decision";
  conflict: boolean;
  /**
   * The card this one replaced, if any. A failed Fix puts it back instead of
   * dropping both cards (rd round 12, B4a-2).
   */
  previous?: DoneCard;
  /** This decision replaced a "Looks right" card, which Undo must restore. */
  replacedReview: boolean;
  /** The posted lines equal the current booking: nothing will change. */
  sameBooking?: boolean;
  /** The Worker says this decision already applied: Undo cannot take it back. */
  cannotUndo?: boolean;
  /** The lines this decision posted: the booking the server will hold. */
  lines?: readonly ExpenseAuditLeg[];
  /** The intercompany pair id the 202 returned for this decision. */
  icPairId?: string | null;
}

/**
 * The one booking this page decides over (rd round 12, B4a-2). Priority:
 * this session's card (an applied card has no pending change) ▸ the Worker's
 * pending `latestDecision` ▸ the legs the page loaded.
 */
interface EffectiveBooking {
  legs: readonly ExpenseAuditLeg[];
  total: bigint | null;
  /**
   * The change still waiting to apply, with the id this page replaces and the
   * pair state the badge reads. Null when nothing is pending.
   */
  pending: {
    lineCount: number;
    icPairId: string | null;
    decisionId: string | null;
    pairState: ExpenseAuditDecisionView["pairState"];
    statusReason: string | null;
  } | null;
  source: "session" | "worker" | "loaded";
  /**
   * The Worker's own latest decision, carried for the pair badge and the
   * pair-state words. It is the same read `effectiveBooking` already made, so
   * no page code reads `latestDecision` a second time.
   */
  decision: ExpenseAuditDecisionView | null;
}

interface FilterState {
  company: CompanyMode;
  from: string;
  to: string;
  viewId: string;
  status: string;
  source: string;
  location: string;
}

interface StoredFilters {
  company?: CompanyMode;
  from?: string;
  to?: string;
  viewId?: string;
  status?: string;
  source?: string;
  location?: string;
}

let detachKey: (() => void) | null = null;

export function keyTargetIsTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return false;
}

// A key is a page shortcut only when focus is on the page itself. Focus on a
// button, a link, a summary, a select, a listbox option or anything else the
// person can operate means the key belongs to that control (Enter activates
// it), so the page must not act on it. This is the native and ARIA interactive
// set; the body, the document and plain containers stay the page's.
const INTERACTIVE_KEY_TARGET =
  'button, a, summary, select, input, textarea, [contenteditable], [tabindex]:not([tabindex="-1"]), ' +
  '[role="button"], [role="link"], [role="option"], [role="combobox"], [role="listbox"], ' +
  '[role="menuitem"], [role="tab"], [role="checkbox"], [role="radio"], [role="switch"], [role="textbox"]';

export function keyTargetIsInteractive(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (keyTargetIsTextEntry(target)) return true;
  return target.closest(INTERACTIVE_KEY_TARGET) !== null;
}

/** Shortest open account whose path ends at this suffix. No silent remap. */
export function resolveQuickPickAccount(
  suffix: string,
  accounts: readonly string[],
): string | null {
  const matches = accounts.filter(
    (account) =>
      auditAccountAllowed(account) &&
      (account === suffix || account.endsWith(`:${suffix}`)),
  );
  if (matches.length === 0) return null;
  matches.sort((a, b) => a.length - b.length);
  return matches[0] ?? null;
}

/** Prefix of the path after the company segment (`Repairs` matches `Repairs:Electrical`). */
export function accountMatchesSuffix(accountText: string, suffix: string): boolean {
  const parts = accountText.split(":");
  if (parts.length < 3) return accountText === suffix || accountText.endsWith(`:${suffix}`);
  const after = parts.slice(2).join(":");
  return after === suffix || after.startsWith(`${suffix}:`) || after.startsWith(suffix);
}

export function auditItemKey(loaded: LoadedItem): string {
  return `${slugText(loaded.company)}:${loaded.item.targetKind}:${loaded.item.targetId}`;
}

/**
 * The internal slug as the plain key a wire-shaped helper takes (a cache key or
 * an image-origin lookup). A Map lookup is the sanctioned slug-to-key
 * boundary: the value is the key itself, never words a person reads.
 */
function slugText(slug: CompanySlug): string {
  return new Map<CompanySlug, string>([[slug, slug]]).get(slug) ?? "";
}

/** The same key boundary for an account path (a suffix match is text work). */
function accountText(account: AccountPath): string {
  return new Map<AccountPath, string>([[account, account]]).get(account) ?? "";
}

function showDetails(detail: string): HTMLElement {
  return h("details", { class: "disclosure", "data-role": "show-details" }, [
    h("summary", {}, ["Show details"]),
    h("p", {}, [amVisibleDisplayText(detail)]),
  ]);
}

export async function renderExpenseAuditPage(opts: ExpenseAuditPageOptions): Promise<void> {
  detachKey?.();
  const stillCurrent = opts.isCurrent ?? (() => true);
  clear(opts.root);

  const role = readerRole(opts.role);
  const ownerMayChange = (): boolean => role === "owner";
  // Owner decision 14: company names come from the shell's rows, and an
  // unknown name reads "this company", never the slug.
  const directory = face.createCompanyDirectory(
    opts.companies.map((company) => ({
      slug: company.slug,
      displayName: company.displayName,
    })),
    { complete: true, segments: opts.companySegments ?? new Map<string, string>() },
  );
  // The page's ONE label context, built once from the same directory its
  // company words come from (rd13 L1).
  const labels = face.accountLabelContext(directory);

  const toast = h("div", { "data-role": "audit-toast", role: "status" });
  const waitNote = h("p", { "data-role": "audit-wait", role: "status" });
  // A failed Undo says so here, not in the shared toast: the next toast write
  // would take the contradiction away while the screen still claimed it
  // (round ten MUST-FIX 1).
  const undoNotice = h("p", { "data-role": "audit-undo-error", role: "alert" });
  // The account list's own failure and Try again live here for the same reason
  // (round ten 4(c)): the toast belongs to whichever action spoke last.
  const chartNotice = h("div", { "data-role": "audit-chart-error", role: "alert" });
  const banner = h("div", { "data-role": "audit-banner", role: "alert" });
  const body = h("div", { "data-role": "audit-body" });
  /**
   * A detached container for a write whose card has already left the screen.
   * `runWrite` disables every control in its `scope` while the request is in the
   * air; for a decision that has repainted to the next item, the card holding
   * the request's own controls is gone, and the card now on screen belongs to
   * the person's next item (and carries the Undo for the one just decided). This
   * sink has no controls, so nothing the person is looking at goes dead
   * (round-2 review M2).
   */
  const writeScopeSink = h("div", { "data-role": "audit-write-scope" });
  opts.root.append(
    h("header", { class: "page-header" }, [
      h("h1", { class: "page-title", "data-role": "audit-title" }, [navLabel("expense-audit")]),
      h("p", { class: "lede" }, [
        "Check each expense. Nothing on this page moves money.",
      ]),
    ]),
    toast,
    waitNote,
    undoNotice,
    chartNotice,
    banner,
    body,
  );
  opts.root.setAttribute("data-prefetch-ready", "false");

  function setBanner(message: string, retry: boolean): void {
    clear(banner);
    if (message === "") return;
    banner.append(document.createTextNode(message));
    if (!retry) return;
    const button = h(
      "button",
      { type: "button", class: "btn btn-primary", "data-action": "retry" },
      ["Try again"],
    );
    button.addEventListener("click", () => {
      void reload().catch((error: unknown) => {
        setBanner(loadErrorText(error), true);
      });
    });
    banner.append(button);
  }

  /**
   * Run one page action and, on a thrown failure, say the one shared sentence
   * with the server's words behind Show details (round 12, A5-2). The write
   * primitive's own outcome is worded where the write happens.
   */
  async function runPageAction(
    live: HTMLElement,
    _label: string,
    work: () => Promise<void>,
  ): Promise<boolean> {
    try {
      await work();
      return stillCurrent();
    } catch (error) {
      if (!stillCurrent()) return false;
      const copy = failureCopy(error, { role, context: "write" });
      clear(live);
      live.append(document.createTextNode(copy.sentence));
      if (copy.detail !== null) live.append(showDetails(copy.detail));
      return false;
    }
  }

  const charts = new Map<CompanySlug, ChartSnapshot>();
  const urlCache = new Map<string, { url: string; contentType: string; expiresAt: string }>();
  let views: ExpenseAuditViewRecord[] = [];
  let summaryUnreviewed: number | null = null;
  let undecided: LoadedItem[] = [];
  /** One number per list read; a response from an older read is dropped. */
  let readSeq = 0;
  let cursors = new Map<CompanySlug, string | null>();
  let sessionDone: DoneCard[] = [];
  let index = 0;
  let fixingKey: string | null = null;
  let splitOpen = false;
  let docIndex = 0;
  let zoomed = false;
  let pastOpen = false;
  let helpOpen = false;
  let conflictText: string | null = null;
  let lineSeq = 0;
  const cancelOnArrival = new Set<string>();
  /**
   * The decision this session already undid. The load-time item still carries
   * the Worker's pending `latestDecision`, so `effectiveBooking` must not offer
   * a change the person just took back (B4a-2). Keyed by decision id, and by
   * item key for a decision that never got an id.
   */
  const cancelledDecisions = new Set<string>();
  const inflight = new Map<string, Promise<void>>();
  /** Writes per item that are waiting their turn and have not posted yet. */
  const queuedWrites = new Map<string, number>();
  /** Document keys whose link request failed or did not come back. */
  const previewFailures = new Map<string, string>();
  /** One link request at a time: paint() can run twice before the first ends. */
  let prefetchTask: Promise<void> | null = null;
  /**
   * A background link mint has no control of its own, so it must not disable
   * the page (a Retry pressed while a mint runs would be swallowed). It still
   * takes the write gate's one flight per key.
   */
  const backgroundScope = document.createElement("div");
  /** A Retry asked for a fresh pass while one was already running. */
  let prefetchAgain = false;

  const scopeList = scopeCompanies();
  let filter = initialFilter(scopeList);
  let hideStrip = readHideStrip();

  /**
   * A write key may act only on an item that has been on screen long enough to
   * read. Round nine A9-H2: the old rule compared only the SAME key, so "1"
   * (which re-categorised this item and drew the next) followed by Enter 120 ms
   * later marked an item nobody had read. So the gate is the ACTIVE ITEM's
   * age, and it covers every write key.
   *
   * A held key is one press however its repeats are timed (round ten MUST-FIX
   * 2: a platform whose first auto-repeat arrives 500 ms after the press still
   * booked a second item). So a key that has not been released cannot act
   * again: every keydown records the key as down and only that key's keyup
   * clears it. The time window stays as the fallback for an event stream that
   * never sends a keyup at all — it must be longer than the slowest first
   * auto-repeat (about 500 ms) and shorter than a deliberate second press.
   */
  const WRITE_KEY_REARM_MS = 300;
  const KEY_RELEASE_FALLBACK_MS = 800;
  let lastWriteKey: { key: string; at: number } | null = null;
  let lastWriteKeyReleased = true;
  let waitTimer: ReturnType<typeof setTimeout> | null = null;
  let activeShownKey: string | null = null;
  let activeShownAt = Date.now();
  let firstItemSinceLoad = true;
  let hasShownAnyItem = false;

  const clearWaitCue = (): void => {
    if (waitTimer !== null) {
      clearTimeout(waitTimer);
      waitTimer = null;
    }
    waitNote.textContent = "";
  };

  /** Says why a key was ignored, and stops saying it once the gate would pass. */
  const showWaitCue = (remainingMs: number): void => {
    waitNote.textContent = WAIT_FOR_ITEM_TEXT;
    if (waitTimer !== null) clearTimeout(waitTimer);
    waitTimer = setTimeout(() => {
      waitTimer = null;
      waitNote.textContent = "";
    }, Math.max(0, remainingMs));
  };

  /**
   * True while the item on screen is too new to act on. Nothing on screen means
   * nothing to wait for (round ten 4(a)); the item the page loaded with was
   * already there before any key was pressed, so a press on it is deliberate.
   */
  const activeItemTooNew = (now: number): boolean =>
    activeLoaded() !== null && !firstItemSinceLoad && now - activeShownAt <= WRITE_KEY_REARM_MS;

  /** Records when the item on screen last changed (reading time starts here). */
  const noteActiveShown = (): void => {
    const active = activeLoaded();
    const key = active === null ? null : auditItemKey(active);
    if (key === activeShownKey) return;
    activeShownKey = key;
    activeShownAt = Date.now();
    // A cue about the item that just left is stale once another one is on
    // screen; a fresh ignored key will say so again.
    clearWaitCue();
    firstItemSinceLoad = !hasShownAnyItem;
    hasShownAnyItem = true;
  };

  const writeKeyArmed = (key: string): boolean => {
    const now = Date.now();
    // The same key without its keyup is the same hold, whenever its repeats
    // arrive. A repeat that carries the browser's own flag never gets here.
    const stillDown =
      lastWriteKey !== null &&
      lastWriteKey.key === key &&
      !lastWriteKeyReleased &&
      now - lastWriteKey.at <= KEY_RELEASE_FALLBACK_MS;
    if (stillDown) {
      lastWriteKey = { key, at: now };
      // Keep the cue truthful: still too new means still say so, otherwise a
      // hold is not a reason to tell the person to wait (round ten 4(b)).
      if (activeItemTooNew(now)) {
        showWaitCue(activeShownAt + WRITE_KEY_REARM_MS - now);
      } else {
        clearWaitCue();
      }
      return false;
    }
    if (activeItemTooNew(now)) {
      // The item is too new to have been read. Say so, and remember this event
      // as the hold's first one so its repeats cannot slip through later.
      lastWriteKey = { key, at: now };
      lastWriteKeyReleased = false;
      showWaitCue(activeShownAt + WRITE_KEY_REARM_MS - now);
      return false;
    }
    lastWriteKey = { key, at: now };
    lastWriteKeyReleased = false;
    clearWaitCue();
    return true;
  };

  const onKey = (event: KeyboardEvent): void => {
    if (!stillCurrent() || !opts.root.isConnected) {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("keyup", onKeyUp);
      return;
    }
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    if (event.defaultPrevented) return;
    const key = event.key;
    // Escape closes the split editor or the category fix from anywhere. Every
    // other key is a page shortcut, and it is the page's only when focus is not
    // on a control: Enter on "Next", "Undo", a quick pick or a sidebar link is
    // that control's own press, never "Looks right".
    if (key !== "Escape" && keyTargetIsInteractive(event.target)) return;
    if (key === "Enter" && event.shiftKey) return;
    if (keyTargetIsTextEntry(event.target)) return;
    const writeKey =
      key === "Enter" ||
      /^[1-9]$/.test(key) ||
      key === "/" ||
      key === "s" ||
      key === "S" ||
      key === "u" ||
      key === "U";
    // A held key is one press, whatever it is: auto-repeat must never book the
    // next item, or undo several cards (round eight A-H2: one held "1" posted
    // for an item that had been on screen for milliseconds).
    if (event.repeat && writeKey) return;
    if (!ownerMayChange() && writeKey) return;
    if (splitOpen) {
      if (key === "Escape") {
        event.preventDefault();
        splitOpen = false;
        paint();
        focusSplitButton();
      }
      return;
    }
    if (key === "Escape" && fixingKey !== null) {
      event.preventDefault();
      fixingKey = null;
      conflictText = null;
      paint();
      return;
    }
    if (key === "Enter") {
      event.preventDefault();
      if (!writeKeyArmed("enter")) return;
      const active = activeLoaded();
      if (active) void runLooksRight(active);
      return;
    }
    if (/^[1-9]$/.test(key)) {
      event.preventDefault();
      if (!writeKeyArmed(key)) return;
      void runQuickPick(Number(key));
      return;
    }
    if (key === "/") {
      event.preventDefault();
      body.querySelector<HTMLInputElement>("[data-role=audit-category] input")?.focus();
      return;
    }
    if (key === "s" || key === "S") {
      event.preventDefault();
      if (!writeKeyArmed(key.toLowerCase())) return;
      openSplit();
      return;
    }
    if (key === "u" || key === "U") {
      event.preventDefault();
      if (!writeKeyArmed(key.toLowerCase())) return;
      void runUndo();
      return;
    }
    if (key === "j" || key === "J" || key === "ArrowRight") {
      event.preventDefault();
      move(1);
      return;
    }
    if (key === "k" || key === "K" || key === "ArrowLeft") {
      event.preventDefault();
      move(-1);
      return;
    }
    if (key === "z" || key === "Z") {
      event.preventDefault();
      zoomed = !zoomed;
      paint();
      return;
    }
    if (key === "," || key === ".") {
      event.preventDefault();
      stepDocument(key === "." ? 1 : -1);
      return;
    }
    if (key === "?") {
      event.preventDefault();
      helpOpen = !helpOpen;
      paint();
    }
  };
  document.addEventListener("keydown", onKey);
  // Only a keyup says a key may act again; a key still down is a hold.
  const onKeyUp = (event: KeyboardEvent): void => {
    if (lastWriteKey !== null && event.key.toLowerCase() === lastWriteKey.key) {
      lastWriteKeyReleased = true;
    }
  };
  document.addEventListener("keyup", onKeyUp);
  detachKey = () => {
    document.removeEventListener("keydown", onKey);
    document.removeEventListener("keyup", onKeyUp);
    clearWaitCue();
  };

  try {
    await reload();
  } catch (error) {
    if (!stillCurrent()) return;
    setBanner(loadErrorText(error), true);
  }

  function scopeCompanies(): Company[] {
    const slugs = resolveScopeMembers(
      opts.scope,
      opts.groups,
      opts.companies.map((company) => company.slug),
    );
    const allowed = new Set(slugs);
    return opts.companies.filter(
      (company) => allowed.has(company.slug) && !company.featurePacks.includes("medical"),
    );
  }

  function initialFilter(list: readonly Company[]): FilterState {
    const stored = readFilters();
    const fallback = defaultCompanyMode(list);
    const company =
      stored.company !== undefined && companyModeValid(stored.company, list)
        ? stored.company
        : fallback;
    return {
      company,
      from: stored.from ?? "",
      to: stored.to ?? "",
      viewId: stored.viewId ?? LOCAL_MAINTENANCE_ID,
      status: stored.status ?? "not_reviewed",
      source: stored.source ?? "",
      location: stored.location ?? "",
    };
  }

  function defaultCompanyMode(list: readonly Company[]): CompanyMode {
    if (opts.scope.kind === "group") return "group";
    if (opts.scope.kind === "company") {
      const slug = opts.scope.slug;
      if (list.some((company) => company.slug === slug)) return slug;
    }
    if (list.some((company) => company.slug === opts.company)) return opts.company;
    return list[0]?.slug ?? opts.company;
  }

  function companyModeValid(mode: CompanyMode, list: readonly Company[]): boolean {
    if (mode === "group" && opts.scope.kind === "group") return true;
    if (mode === "all") return true;
    return list.some((company) => company.slug === mode);
  }

  function selectedCompanies(): Company[] {
    const list = scopeCompanies();
    if (filter.company === "group" || filter.company === "all") return list;
    const one = list.find((company) => company.slug === filter.company);
    if (one) return [one];
    const fallback = list[0];
    return fallback ? [fallback] : [];
  }

  async function reload(): Promise<void> {
    const seq = (readSeq += 1);
    clear(banner);
    undoNotice.textContent = "";
    const companies = selectedCompanies();
    const [viewRows, summaryBody, pages] = await Promise.all([
      // The category groups are the owner's (the Worker refuses anyone else),
      // so an account manager does not ask and cannot collect a 403 per load.
      ownerMayChange()
        ? opts.client.listExpenseAuditViews().catch(() => [] as ExpenseAuditViewRecord[])
        : Promise.resolve([] as ExpenseAuditViewRecord[]),
      opts.client
        .getExpenseAuditSummary(summaryScope(), summaryId())
        .catch(() => null),
      // rd13 L1: the cards are asked for before the group list lands, so the
      // first ask cannot name a company-owned group. The cards are still
      // filtered by the group in `matchesClientFilter`, which reads the list.
      Promise.all(companies.map((company) => fetchPage(company.slug, null))),
    ]);
    // R5-07: an older filter's answer must not overwrite a newer filter's
    // list. The newest reload owns the page; a stale answer changes nothing.
    if (!stillCurrent() || seq !== readSeq) return;
    views = mergeViews(viewRows);
    previewFailures.clear();
    summaryUnreviewed = unreviewedCount(
      summaryBody,
      companies.map((company) => company.slug),
      currentViewName(),
    );
    undecided = [];
    cursors = new Map();
    for (const page of pages) {
      cursors.set(page.company, page.nextAfter);
      for (const item of page.items) undecided.push({ company: page.company, item });
    }
    // A card this session already made stays out of the "Not reviewed" pile,
    // so a reload cannot re-offer work just done. The other filters exist to
    // list those items (Reviewed / Changed by me / All), so they are not
    // filtered here — that exclusion used to hide the very item asked for.
    const doneKeys =
      filter.status === "not_reviewed"
        ? new Set(sessionDone.map((card) => auditItemKey(card.loaded)))
        : new Set<string>();
    undecided = undecided.filter(
      (loaded) => matchesClientFilter(loaded) && !doneKeys.has(auditItemKey(loaded)),
    );
    index = 0;
    fixingKey = null;
    splitOpen = false;
    docIndex = 0;
    await ensureCharts(companies.map((company) => company.slug));
    if (!stillCurrent() || seq !== readSeq) return;
    paint();
  }

  async function fetchPage(
    company: CompanySlug,
    after: string | null,
  ): Promise<{ company: CompanySlug; items: ExpenseAuditItem[]; nextAfter: string | null }> {
    const query: {
      from?: string;
      to?: string;
      view?: string;
      status?: string;
      source?: string;
      location?: string;
      after?: string;
    } = {};
    const from = monthStart(filter.from);
    const to = monthEnd(filter.to);
    if (from) query.from = from;
    if (to) query.to = to;
    const viewId = effectiveViewId();
    if (viewId && viewId !== LOCAL_MAINTENANCE_ID && viewId !== "all") {
      query.view = viewId;
    }
    if (filter.status) query.status = filter.status;
    if (filter.source) query.source = filter.source;
    if (filter.location) query.location = filter.location;
    if (after) query.after = after;
    const page = await opts.client.listExpenseAuditItems(company, query);
    return { company, items: page.items, nextAfter: page.nextAfter };
  }

  function summaryScope(): "company" | "group" | "all" {
    if (filter.company === "all") return "all";
    if (filter.company === "group" && opts.scope.kind === "group") return "group";
    return "company";
  }

  function summaryId(): string {
    if (filter.company === "group" && opts.scope.kind === "group") return opts.scope.groupId;
    if (filter.company === "all") return "";
    return slugText(filter.company);
  }

  function showChartFailure(slug: CompanySlug): void {
    clear(chartNotice);
    const retry = h("button", {
      type: "button",
      class: "btn btn-secondary",
      "data-action": "chart-retry",
    }, ["Try again"]);
    retry.addEventListener("click", () => {
      clear(chartNotice);
      void ensureCharts([slug]).then(() => {
        if (stillCurrent()) paint();
      });
    });
    chartNotice.append(
      "The account list could not be loaded.",
      retry,
      showDetails(slugText(slug)),
    );
  }

  async function ensureCharts(slugs: readonly string[]): Promise<void> {
    await Promise.all(
      slugs.map(async (slug) => {
        if (charts.has(slug)) return;
        try {
          charts.set(slug, await opts.client.getChartSnapshot(slug));
        } catch {
          if (stillCurrent()) showChartFailure(slug);
        }
      }),
    );
    if (stillCurrent() && slugs.every((slug) => charts.has(slug))) clear(chartNotice);
  }

  function paint(): void {
    if (!stillCurrent()) return;
    noteActiveShown();
    draw();
    prefetchOnce();
  }

  function draw(): void {
    clear(body);
    if (!ownerMayChange()) {
      // The page's own controls are the owner's. An account manager is not
      // told a control is missing: they read the change control mounted beside
      // the active item below (owner decision 5, rd15 lane B).
      body.append(
        h("p", { class: "muted", "data-role": "owner-only-note", id: OWNER_ONLY_NOTE_ID }, [
          "You can change this line's category beside the item. The other audit controls are the owner's.",
        ]),
      );
    }
    body.append(renderFilters());
    const active = activeLoaded();
    if (!active) {
      body.append(
        h("div", { class: "empty-state", "data-role": "audit-empty", tabindex: "-1" }, [
          "Nothing to review in this filter. Widen the dates or choose All to see more.",
        ]),
      );
      body.append(renderJustDone());
      body.append(renderPast());
      return;
    }
    body.append(renderActive(active));
    if (!ownerMayChange()) body.append(renderChangeCategory(active));
    body.append(renderJustDone());
    body.append(renderPast());
    body.append(renderKeyStrip());
    if (helpOpen) body.append(renderHelp());
  }

  /**
   * The account manager's one control on this page (owner decision 5, rd15
   * lane B). Every other control here is the owner's; an account manager
   * changes one line's category through the shared module, which lane A can
   * also mount on a transaction's detail. A correction is a new entry, and a
   * closed month stays the owner's — both decided by the Worker, worded by the
   * module.
   */
  function renderChangeCategory(loaded: LoadedItem): HTMLElement {
    const item = loaded.item;
    // ONE reader of what is booked (B4a-3): the legs and the pending change
    // come from `effectiveBooking`, never from the item's raw fields.
    const booking = effectiveBooking(loaded);
    // A pending record with no id is not a change this reader can cancel;
    // `effectiveBooking` can carry an empty id for a card whose read lost it.
    const pendingId = booking.pending?.decisionId ?? null;
    const pending =
      pendingId === null ? null : { decisionId: pendingId, legs: booking.legs };
    // The module owns the three writes (record, cancel, mint images), so each
    // runs inside the shared write primitive with one failure wording.
    const handle = mountChangeCategory({
      role,
      item: {
        company: loaded.company,
        targetId: item.targetId,
        legsSha256: item.legsSha256,
        periodLocked: item.periodLocked,
        legs: booking.legs.map((leg) => ({
          account: leg.account,
          location: leg.location,
          amount: leg.amount,
        })),
        documentIds: item.documents.map((doc) => doc.documentId),
        pending,
      },
      accounts: accountsFor(loaded.company),
      locations: locationsFor(loaded.company),
      labelCompanies: labels,
      client: opts.client,
      reload: async () => {
        await reload();
      },
      announce: (message) => {
        setBanner(message, false);
      },
    });
    return handle.root;
  }

  function renderFilters(): HTMLElement {
    const bar = h("div", { "data-role": "audit-filters" });
    const company = h("select", {
      "data-role": "audit-company",
      "aria-label": "Company",
    });
    if (opts.scope.kind === "group") {
      company.append(h("option", { value: "group" }, ["This group"]));
    }
    company.append(h("option", { value: "all" }, ["All companies"]));
    for (const row of scopeCompanies()) {
      const optionLabel = labelFor(row.slug);
      company.append(h("option", { value: row.slug }, [optionLabel]));
    }
    company.value = filter.company;
    company.addEventListener("change", () => {
      filter = { ...filter, company: company.value, location: "" };
      rememberFilters();
      void reload().catch((error: unknown) => {
        setBanner(loadErrorText(error), true);
      });
    });

    const from = monthInput("audit-from", "From", filter.from);
    const to = monthInput("audit-to", "To", filter.to);
    from.addEventListener("change", () => {
      filter = { ...filter, from: from.value };
      rememberFilters();
      void reload().catch((error: unknown) => {
        setBanner(loadErrorText(error), true);
      });
    });
    to.addEventListener("change", () => {
      filter = { ...filter, to: to.value };
      rememberFilters();
      void reload().catch((error: unknown) => {
        setBanner(loadErrorText(error), true);
      });
    });

    const view = h("select", { "data-role": "audit-view", "aria-label": "Category group" });
    view.append(h("option", { value: "all" }, ["All expenses"]));
    for (const row of viewChoices()) {
      view.append(h("option", { value: row.id }, [amVisibleDisplayText(row.name)]));
    }
    const selectedView = effectiveViewId();
    if ([...view.options].some((option) => option.value === selectedView)) {
      view.value = selectedView;
    } else {
      view.value = "all";
    }
    view.addEventListener("change", () => {
      filter = { ...filter, viewId: view.value };
      rememberFilters();
      void reload().catch((error: unknown) => {
        setBanner(loadErrorText(error), true);
      });
    });

    const status = h("select", { "data-role": "audit-status", "aria-label": "Status" });
    for (const [value, label] of [
      ["not_reviewed", "Not reviewed"],
      ["changed", "Changed by me"],
      ["reviewed", "Reviewed"],
      ["all", "All"],
    ] as const) {
      status.append(h("option", { value }, [label]));
    }
    status.value = filter.status;
    status.addEventListener("change", () => {
      filter = { ...filter, status: status.value };
      rememberFilters();
      void reload().catch((error: unknown) => {
        setBanner(loadErrorText(error), true);
      });
    });

    const source = h("select", { "data-role": "audit-source", "aria-label": "Source" });
    source.append(h("option", { value: "" }, ["Any source"]));
    source.append(h("option", { value: "receipt" }, ["Receipt"]));
    source.append(h("option", { value: "invoice" }, ["Invoice"]));
    source.append(h("option", { value: "no_document" }, ["No document"]));
    source.value = filter.source;
    source.addEventListener("change", () => {
      filter = { ...filter, source: source.value };
      rememberFilters();
      void reload().catch((error: unknown) => {
        setBanner(loadErrorText(error), true);
      });
    });

    bar.append(company, from, to, view, status, source);
    if (showAddressFilter()) {
      const address = h("select", { "data-role": "audit-address", "aria-label": "Address" });
      address.append(h("option", { value: "" }, ["All addresses"]));
      const slug = filter.company === "sor-properties" ? "sor-properties" : selectedCompanies()[0]?.slug;
      const locations = slug ? locationsFor(slug) : [];
      for (const location of locations) {
        address.append(h("option", { value: location }, [amVisibleDisplayText(location)]));
      }
      address.value = filter.location;
      address.addEventListener("change", () => {
        filter = { ...filter, location: address.value };
        rememberFilters();
        void reload().catch((error: unknown) => {
          setBanner(loadErrorText(error), true);
        });
      });
      bar.append(address);
    }
    bar.append(h("span", { "data-role": "audit-counter" }, [`${toGoText()} to go`]));
    if (!filter.from && !filter.to) {
      bar.append(h("span", { class: "muted" }, ["Sorted oldest first, up to today."]));
    }
    if (ownerMayChange()) bar.append(renderViewEditor());
    return bar;
  }

  function renderViewEditor(): HTMLElement {
    const current = viewChoices().find((row) => row.id === filter.viewId);
    const editor = h("details", { "data-role": "audit-view-editor" });
    editor.append(h("summary", {}, ["Edit category group"]));
    const name = h("input", {
      type: "text",
      "data-role": "audit-view-name",
      "aria-label": "Group name",
      value: amVisibleDisplayText(current?.name ?? "Maintenance"),
    });
    const suffixes = h("textarea", {
      "data-role": "audit-view-suffixes",
      "aria-label": "Account suffixes",
    });
    suffixes.value = (current?.accountSuffixes ?? [...DEFAULT_MAINTENANCE_SUFFIXES])
      .map((line) => amVisibleDisplayText(line))
      .join("\n");
    const picks = h("textarea", {
      "data-role": "audit-view-picks",
      "aria-label": "Quick picks",
    });
    picks.value = (current?.quickPicks ?? DEFAULT_QUICK_PICKS.map((pick) => pick.suffix))
      .map((line) => amVisibleDisplayText(line))
      .join("\n");
    const save = h("button", { type: "button", "data-role": "audit-view-save" }, ["Save group"]);
    save.addEventListener("click", () => {
      const accountSuffixes = textLines(suffixes.value);
      const quickPicks = textLines(picks.value);
      // The Worker's own limits. A group over them is refused with the limit
      // named, never silently cut down, and a blank name is not "Maintenance"
      // (R5-10).
      if (name.value.trim().length === 0) {
        toast.textContent = "Give the category group a name.";
        return;
      }
      if (accountSuffixes.length > 50) {
        toast.textContent = "A category group can hold up to 50 account suffixes.";
        return;
      }
      if (quickPicks.length > 9) {
        toast.textContent = "A category group can hold up to 9 quick picks.";
        return;
      }
      const groupName = name.value.trim();
      const selected = effectiveViewId();
      const serverId =
        selected !== LOCAL_MAINTENANCE_ID && selected !== "all" ? selected : undefined;
      // One press, one request: the write gate refuses a second press while the
      // first is in flight, and the re-read belongs to the landed write
      // (rd round 12, B4a-5).
      void (async () => {
        const outcome = await runWrite({
          key: "expense-audit-view-save",
          scope: editor,
          gate: () => null,
          send: () =>
            serverId === undefined
              ? opts.client.putExpenseAuditView({
                  name: groupName,
                  accountSuffixes,
                  quickPicks,
                })
              : opts.client.putExpenseAuditView(
                  { name: groupName, accountSuffixes, quickPicks },
                  serverId,
                ),
          afterLanded: async (saved) => {
            rememberView({ ...saved, accountSuffixes, quickPicks });
            filter = { ...filter, viewId: saved.id };
            rememberFilters();
            await reload();
          },
          stillCurrent,
        });
        if (!stillCurrent()) return;
        clear(toast);
        if (outcome.kind !== "landed") {
          const copy =
            outcome.error !== undefined
              ? failureCopy(outcome.error, { role, context: "write" })
              : { sentence: outcomeSentence(outcome, role), detail: null };
          toast.append(document.createTextNode(copy.sentence));
          if (copy.detail !== null) toast.append(showDetails(copy.detail));
          return;
        }
        toast.append(
          document.createTextNode(
            outcome.afterLandedFailed === true
              ? "Saved. The list did not reload."
              : outcomeSentence(outcome, role),
          ),
        );
      })();
    });
    editor.append(name, suffixes, picks, save);
    return editor;
  }

  function monthInput(role: string, label: string, value: string): HTMLInputElement {
    const input = h("input", {
      type: "month",
      "data-role": role,
      "aria-label": label,
      value,
    });
    return input;
  }

  function renderActive(loaded: LoadedItem): HTMLElement {
    // The card takes focus after "Looks right" so the next item is the one the
    // person is on (tabindex -1 keeps it out of the Tab order and out of the
    // page's own key-target rule).
    const card = h("article", { "data-role": "audit-active", tabindex: "-1" });
    const item = loaded.item;
    const row = h("div", { "data-role": "audit-card-row" });
    row.append(renderDocument(loaded), renderFacts(loaded));
    card.append(row);
    if (item.periodLocked) {
      card.append(
        h("p", { "data-role": "audit-closed-month" }, [
          `${formatMonthYear(item.date)} — closed month (fix posts there, cash untouched)`,
        ]),
      );
    }
    if (conflictText) {
      card.append(h("p", { "data-role": "audit-conflict" }, [conflictText]));
    }
    const position = h("p", { "data-role": "audit-position" }, [
      `${String(index + 1)} of ${toGoText()}`,
    ]);
    const prev = h("button", { type: "button", "data-role": "audit-prev" }, ["Previous"]);
    const next = h("button", { type: "button", "data-role": "audit-next" }, ["Next"]);
    prev.addEventListener("click", () => {
      move(-1);
    });
    next.addEventListener("click", () => {
      move(1);
    });
    card.append(position, h("div", {}, [prev, next]));
    if (splitOpen) {
      card.append(renderSplit(loaded));
    }
    return card;
  }

  function renderDocument(loaded: LoadedItem): HTMLElement {
    const panel = h("div", { "data-role": "audit-document" });
    const docs = [...loaded.item.documents].sort((a, b) => a.seq - b.seq);
    const hub = hubLink(loaded);
    if (docs.length === 0) {
      panel.append(h("p", {}, ["No document"]));
      if (hub) panel.append(hub);
      return panel;
    }
    const current = docs[Math.min(docIndex, docs.length - 1)];
    if (!current) {
      panel.append(h("p", {}, ["No document"]));
      return panel;
    }
    if (!ownerMayChange()) {
      // The Worker gives document links to the owner only, so none is asked for.
      panel.append(
        h("p", { class: "muted", "data-role": "audit-document-owner-only" }, [
          advice("view_expense_audit", role),
        ]),
      );
      return panel;
    }
    const documentKey = previewKey(slugText(loaded.company), current.documentId);
    const failureDetail = previewFailures.get(documentKey);
    if (failureDetail !== undefined) {
      // Round nine A9-M5: the panel used to say "Loading preview…" for ever,
      // or blame the audit list. Both were false; this is the one honest state.
      const retry = h("button", {
        type: "button",
        class: "btn btn-secondary",
        "data-role": "audit-document-retry",
      }, ["Retry"]);
      retry.addEventListener("click", () => {
        previewFailures.delete(documentKey);
        paint();
        // Ask again for this document even if a link pass is already running.
        prefetchOnce(true);
      });
      panel.append(
        h("p", { "data-role": "audit-document-unavailable" }, [
          sentence("preview_unavailable", role),
        ]),
        retry,
        showDetails(failureDetail),
      );
      appendDocumentFooter(panel, docs);
      if (hub) panel.append(hub);
      return panel;
    }
    const signed = urlCache.get(documentKey);
    if (!signed || !urlFresh(signed.expiresAt)) {
      panel.append(h("p", { "data-role": "audit-document-pending" }, ["Loading preview…"]));
    } else {
      const href = safeSignedUrl(signed.url);
      if (href === null) {
        panel.append(h("p", {}, [sentence("preview_unavailable", role)]));
      } else if (signed.contentType === "application/pdf") {
        const frame = h("iframe", {
          "data-role": "audit-document-frame",
          title: "Document preview",
          src: pdfSrc(href),
          "data-zoomed": zoomed ? "true" : "false",
        });
        panel.append(frame);
      } else if (signed.contentType === "image/jpeg" || signed.contentType === "image/png") {
        const image = h("img", {
          "data-role": "audit-document-frame",
          alt: "Document preview",
          src: href,
          "data-zoomed": zoomed ? "true" : "false",
        });
        image.addEventListener("click", () => {
          zoomed = !zoomed;
          paint();
        });
        panel.append(image);
      } else {
        panel.append(h("p", {}, ["This preview type cannot be shown."]));
      }
    }
    appendDocumentFooter(panel, docs);
    if (hub) panel.append(hub);
    return panel;
  }

  /** "Document 2 of 3", when there is more than one. */
  function appendDocumentFooter(panel: HTMLElement, docs: readonly { seq: number }[]): void {
    if (docs.length <= 1) return;
    panel.append(
      h("p", { class: "muted" }, [
        `Document ${String(Math.min(docIndex, docs.length - 1) + 1)} of ${String(docs.length)}`,
      ]),
    );
  }

  function hubLink(loaded: LoadedItem): HTMLAnchorElement | null {
    if (!ownerMayChange()) return null;
    const imageUrl = loaded.item.receipt?.imageUrl;
    if (!imageUrl) return null;
    const href = safeReceiptImageHref(imageUrl, slugText(loaded.company));
    if (href === null) return null;
    return h(
      "a",
      { href, target: "_blank", rel: "noopener noreferrer", "data-role": "audit-hub-link" },
      ["Open the original receipt"],
    );
  }

  function renderFacts(loaded: LoadedItem): HTMLElement {
    const item = loaded.item;
    const side = h("div", { "data-role": "audit-facts" });
    side.append(
      h("p", { "data-role": "audit-payee" }, [safeText(payeeText(item))]),
      h("p", { "data-role": "audit-amount", class: "money" }, [formatMoney(item.bankAmount)]),
      h("p", {}, [
        safeText(
          [formatAuditDate(item.date), item.accountMask ? `••${amVisibleDisplayText(item.accountMask)}` : ""]
            .filter((part) => part.length > 0)
            .join(" · "),
        ),
      ]),
    );
    if (item.receipt) {
      const receipt = item.receipt;
      const staff = [receipt.expenseType, receipt.subCategory].filter((part) => part && part.length > 0);
      side.append(h("p", {}, ["What was recorded"]));
      side.append(
        h("p", { "data-role": "audit-staff" }, [
          safeText(
            [
              staff.join(" › "),
              receipt.property,
              receipt.address ?? "",
              receipt.note ?? "",
              receipt.submittedBy ? `submitted by ${receipt.submittedBy}` : "",
            ]
              .map((part) => amVisibleDisplayText(part))
              .filter((part) => part.length > 0)
              .join(" · "),
          ),
        ]),
      );
      // The hub's own account name, as sent. Untrusted text: safeText only.
      if (receipt.glAccount !== undefined) {
        side.append(
          h("p", { "data-role": "audit-hub-account" }, [
            safeText(
              `Account from the receipt: ${amVisibleDisplayText(receipt.glAccount)}` +
                (receipt.glParent === undefined
                  ? ""
                  : ` (${amVisibleDisplayText(receipt.glParent)})`),
            ),
          ]),
        );
      }
    }
    if (item.billLines.length > 0) {
      side.append(h("p", {}, ["Invoice lines"]));
      for (const line of item.billLines) {
        side.append(
          h("p", { "data-role": "audit-bill-line" }, [
            safeText(
              [line.property, line.unit ?? "", line.hubAccount, line.description, line.amount, line.paidBy ?? ""]
                .map((part) => amVisibleDisplayText(part))
                .filter((part) => part.length > 0)
                .join(" · "),
            ),
          ]),
        );
      }
    }
    side.append(
      h("p", {}, [
        `Booked as${item.originalSource ? ` (${amVisibleDisplayText(item.originalSource)})` : ""}`,
      ]),
    );
    const booking = effectiveBooking(loaded);
    for (const leg of booking.legs) {
      const amount = moneyText(leg.amount, { fallback: "No amount" });
      const words = [auditAccountWords(loaded.company, leg.account), amount, amVisibleDisplayText(leg.location)]
        .filter((part) => part.length > 0)
        .join(" · ");
      side.append(
        h("p", { "data-role": "audit-booked-as" }, [
          safeText(words),
        ]),
      );
    }
    const badge = pairBadgeText(booking);
    if (badge) {
      side.append(
        h("div", { "data-role": "audit-pair-badge" }, [
          badge.face,
          badge.detail === null ? null : showDetails(badge.detail),
        ]),
      );
    }
    side.append(h("p", {}, ["Change to"]));
    side.append(renderQuickPicks(loaded));
    side.append(renderCategory(loaded));
    const looks = h("button", { type: "button", "data-role": "audit-looks-right" }, [
      "Looks right ⏎",
    ]);
    // Round-11 decision 5 (deferred): a paired Worker decision after a reload
    // cannot seed the editor honestly, so Split is replaced by the one
    // sentence instead of a control that would invent the other company.
    const splitBlocked = pairedSplitCannotOpen(booking);
    const split = splitBlocked
      ? h("p", { class: "muted", "data-role": "audit-split-paired" }, [PAIRED_SPLIT_TEXT])
      : h("button", { type: "button", "data-role": "audit-split" }, ["Split S"]);
    const undo = h("button", { type: "button", "data-role": "audit-undo" }, ["Undo U"]);
    if (!ownerMayChange()) {
      looks.disabled = true;
      undo.disabled = true;
      if (split instanceof HTMLButtonElement) split.disabled = true;
      for (const control of [looks, split, undo]) {
        control.setAttribute("aria-describedby", OWNER_ONLY_NOTE_ID);
      }
    }
    looks.addEventListener("click", () => {
      void runLooksRight(loaded);
    });
    if (split instanceof HTMLButtonElement) {
      split.addEventListener("click", () => {
        openSplit();
      });
    }
    undo.addEventListener("click", () => {
      void runUndo();
    });
    // Looks right is stored on the server and the Worker has no way to take it
    // back, so Undo is not offered while it is the last thing done (round eight
    // A-M4: Undo used to put the item back on this page and send nothing, while
    // the server kept it reviewed).
    const lastWasLooksRight = sessionDone[0]?.kind === "review";
    // Undo is not offered where Undo cannot succeed: the Worker refuses the
    // cancel of a decision the pipeline already applied (R5-08).
    const lastCannotUndo = sessionDone[0]?.cannotUndo === true;
    side.append(
      h("div", { class: "actions" }, [
        looks,
        split,
        ...(lastWasLooksRight || lastCannotUndo ? [] : [undo]),
      ]),
    );
    return side;
  }

  /**
   * The quick-pick numbers that exist for a company: a pick with an account of
   * that company behind it. A pick with no matching account is not drawn and
   * the key legend does not promise it (round eight M3: picks 2 to 9 were
   * greyed out with no reason while the legend said "1-9 quick pick").
   */
  function availableQuickPicks(company: CompanySlug): number[] {
    const accounts = accountsFor(company);
    const numbers: number[] = [];
    quickPicksForFilter().forEach((pick, offset) => {
      if (pick !== null && resolveQuickPickAccount(pick.suffix, accounts) !== null) {
        numbers.push(offset + 1);
      }
    });
    return numbers;
  }

  function renderQuickPicks(loaded: LoadedItem): HTMLElement {
    const wrap = h("div", { "data-role": "audit-quick-picks" });
    const chartLoaded = charts.has(loaded.company);
    const accounts = chartLoaded ? accountsFor(loaded.company) : [];
    quickPicksForFilter().forEach((pick, offset) => {
      const number = offset + 1;
      const resolved = pick ? resolveQuickPickAccount(pick.suffix, accounts) : null;
      if (pick === null || resolved === null) return;
      // A pick whose account has a twin is named by that account, so
      // "1 Repairs" cannot hide which of two Repairs accounts it posts.
      const pickWords = `${String(number)} ${auditAccountWords(loaded.company, resolved, pick.name)}`;
      const button = h("button", {
        type: "button",
        "data-role": "audit-quick-pick",
        "data-key": String(number),
      }, [pickWords]);
      if (!ownerMayChange()) {
        button.disabled = true;
        button.setAttribute("aria-describedby", OWNER_ONLY_NOTE_ID);
      }
      button.addEventListener("click", () => {
        void runRecategorize(loaded, resolved, pick.name);
      });
      wrap.append(button);
    });
    const emptyReason = quickPickNoteText(chartLoaded, wrap.childElementCount, accounts.length);
    if (emptyReason !== null) {
      wrap.append(
        h("p", { class: "muted", "data-role": "audit-no-quick-picks" }, [emptyReason]),
      );
    }
    return wrap;
  }

  function renderCategory(loaded: LoadedItem): HTMLElement {
    const accounts = accountsFor(loaded.company);
    const current = singleAccount(effectiveBooking(loaded).legs);
    const chooser = mountChartChooser({
      kind: "account",
      selectable: accounts,
      companies: labels,
      className: "account-input",
      ariaLabel: "Category",
      placeholder: "Type or pick an account…",
      ...(current !== null && accounts.includes(current) ? { initialValue: current } : {}),
    });
    chooser.root.setAttribute("data-role", "audit-category");
    const submit = (account: string | null): void => {
      if (account === null || account === current) return;
      void runRecategorize(loaded, account, leaf(account, labels));
    };
    chooser.filter.addEventListener("keydown", (event) => {
      if (event.key !== "Enter") return;
      submit(chooser.getValue());
    });
    chooser.root.addEventListener("mousedown", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      const name = target.closest("[data-name]")?.getAttribute("data-name");
      if (name) submit(name);
    });
    if (!ownerMayChange()) {
      chooser.root.querySelectorAll("input, button, select").forEach((node) => {
        if (node instanceof HTMLInputElement || node instanceof HTMLButtonElement || node instanceof HTMLSelectElement) {
          node.disabled = true;
          node.setAttribute("aria-describedby", OWNER_ONLY_NOTE_ID);
        }
      });
    }
    const wrap = h("div", { class: "field" }, [
      h("span", { class: "field-label" }, ["Category"]),
      chooser.root,
    ]);
    return wrap;
  }

  function renderSplit(loaded: LoadedItem): HTMLElement {
    const booking = effectiveBooking(loaded);
    const editor = mountSplitEditor({
      totalCents: booking.total ?? 0n,
      lines: splitSeed(loaded),
      companies: splitCompanies(loaded.company),
      labelCompanies: labels,
      locationsFor,
      accountsFor,
      onSave: (lines) => {
        void runSplit(loaded, lines);
      },
      onCancel: () => {
        splitOpen = false;
        paint();
        focusSplitButton();
      },
    });
    return editor.root;
  }

  function renderJustDone(): HTMLElement {
    const slot = h("div", { "data-role": "audit-just-done" });
    const card = sessionDone[0];
    if (!card || auditItemKey(card.loaded) === fixingKey) return slot;
    const row = h("p", {}, [
      safeText(payeeText(card.loaded.item)),
      " ",
      formatMoney(card.loaded.item.bankAmount),
      " ",
      safeText(amVisibleDisplayText(card.changeLabel)),
    ]);
    if (!card.conflict && card.kind === "decision" && card.sameBooking !== true) {
      row.append(" This will show in the books after the next update.");
    }
    if (card.conflict) {
      row.append(" This item changed before your fix was posted — please look again");
    }
    if (!card.conflict && card.kind === "review") {
      row.append(" This cannot be undone here. Use Fix to change the category.");
    }
    const fix = h("button", { type: "button", class: "btn btn-secondary", "data-role": "audit-fix" }, ["Fix"]);
    if (!ownerMayChange()) {
      fix.disabled = true;
      fix.setAttribute("aria-describedby", OWNER_ONLY_NOTE_ID);
    }
    fix.addEventListener("click", () => {
      beginFix(card);
    });
    slot.append(row, fix);
    slot.addEventListener("click", (event) => {
      if (event.target === fix) return;
      beginFix(card);
    });
    return slot;
  }

  function renderPast(): HTMLElement {
    const wrap = h("div", { "data-role": "audit-past" });
    const toggle = h("button", {
      type: "button",
      "data-role": "audit-past-approvals",
    }, [`See past approvals (${String(sessionDone.length)})`]);
    toggle.addEventListener("click", () => {
      pastOpen = !pastOpen;
      paint();
    });
    wrap.append(toggle);
    if (!pastOpen) return wrap;
    const list = h("div", { "data-role": "audit-past-list" });
    for (const card of sessionDone) {
      const row = h("p", { "data-role": "audit-past-row" }, [
        safeText(amVisibleDisplayText(`${payeeText(card.loaded.item)} ${card.changeLabel}`)),
      ]);
      const fix = h("button", { type: "button", class: "btn btn-secondary", "data-role": "audit-fix" }, ["Fix"]);
      if (!ownerMayChange()) {
        fix.disabled = true;
        fix.setAttribute("aria-describedby", OWNER_ONLY_NOTE_ID);
      }
      fix.addEventListener("click", () => {
        beginFix(card);
      });
      list.append(row, fix);
    }
    wrap.append(list);
    return wrap;
  }

  /** "1", "1–4" or "1, 3": the quick-pick keys that work, as the legend names them. */
  function quickPickKeysText(numbers: readonly number[]): string {
    const runs: string[] = [];
    for (let i = 0; i < numbers.length; ) {
      let j = i;
      while (numbers[j + 1] === (numbers[j] ?? 0) + 1) j += 1;
      const first = String(numbers[i]);
      runs.push(j > i + 1 ? `${first}–${String(numbers[j])}` : j === i + 1 ? `${first}, ${String(numbers[j])}` : first);
      i = j + 1;
    }
    return runs.join(", ");
  }

  function activeQuickPickNumbers(): number[] {
    const active = activeLoaded();
    return active === null ? [] : availableQuickPicks(active.company);
  }

  function renderKeyStrip(): HTMLElement {
    if (hideStrip) return h("p", { "data-role": "audit-key-strip" });
    // Only keys that work: an account manager cannot change anything, and a
    // pick with no account behind it is not a key.
    if (!ownerMayChange()) {
      return h("p", { "data-role": "audit-key-strip" }, ["J or → next · K or ← previous · ? all keys"]);
    }
    const picks = activeQuickPickNumbers();
    const parts = [
      "⏎ looks right",
      ...(picks.length > 0 ? [`${quickPickKeysText(picks)} quick pick`] : []),
      "/ category",
      "S split",
      "U undo",
      "? all keys",
    ];
    return h("p", { "data-role": "audit-key-strip" }, [parts.join(" · ")]);
  }

  function renderHelp(): HTMLElement {
    const panel = h("div", { "data-role": "audit-keys-help" });
    const picks = activeQuickPickNumbers();
    const writes = ownerMayChange()
      ? [
          "Enter looks right.",
          ...(picks.length > 0 ? [`${quickPickKeysText(picks)} recategorize.`] : []),
          "/ category. S split. U undo.",
        ]
      : [];
    panel.append(
      h("p", {}, [
        [
          ...writes,
          "J or → next. K or ← previous. Z zoom. , and . step through documents. ? this list.",
        ].join(" "),
      ]),
    );
    const hide = h("button", { type: "button", "data-role": "audit-hide-strip" }, [
      hideStrip ? "Show key strip" : "Hide key strip",
    ]);
    hide.addEventListener("click", () => {
      hideStrip = !hideStrip;
      writeHideStrip(hideStrip);
      paint();
    });
    panel.append(hide);
    return panel;
  }

  function activeLoaded(): LoadedItem | null {
    if (fixingKey !== null) {
      const card = sessionDone.find((done) => auditItemKey(done.loaded) === fixingKey);
      if (card) return card.loaded;
    }
    return undecided[index] ?? null;
  }

  function move(delta: number): void {
    if (fixingKey !== null) fixingKey = null;
    const next = index + delta;
    if (next < 0 || next >= undecided.length) return;
    index = next;
    docIndex = 0;
    zoomed = false;
    splitOpen = false;
    conflictText = null;
    paint();
    maybeLoadMore();
  }

  function stepDocument(delta: number): void {
    const active = activeLoaded();
    if (!active) return;
    const count = active.item.documents.length;
    if (count === 0) return;
    docIndex = Math.min(count - 1, Math.max(0, docIndex + delta));
    paint();
  }

  function openSplit(): void {
    if (!ownerMayChange()) return;
    const active = activeLoaded();
    if (!active) return;
    // An item with no readable booking lines cannot seed a split: opening an
    // empty editor would ask the person to invent lines for money the page
    // could not describe. Say so instead.
    const booking = effectiveBooking(active);
    if (booking.legs.length === 0) {
      toast.textContent = NO_BOOKING_LINES_TEXT;
      return;
    }
    if (pairedSplitCannotOpen(booking)) {
      // Round-11 decision 5's contract change is deferred: do not open an
      // editor that would invent the other company's line.
      toast.textContent = PAIRED_SPLIT_TEXT;
      return;
    }
    splitOpen = true;
    void ensureCharts(splitCompanies(active.company).map((company) => company.slug)).then(() => {
      if (!stillCurrent()) return;
      paint();
      // S opens the editor on the first amount: the first thing a split needs
      // (R5-09), instead of leaving focus on the page body.
      focusFirstSplitAmount();
    });
  }

  /** The first field a split needs: its first line's amount. */
  function focusFirstSplitAmount(): void {
    activeCard()?.querySelector<HTMLInputElement>("[data-role=split-amount]")?.focus();
  }

  /** Closing Split returns focus to the control that opened it (R5-09). */
  function focusSplitButton(): void {
    const button = body.querySelector<HTMLElement>('[data-role="audit-split"]');
    if (button !== null) button.focus();
    else focusAfterPaint();
  }

  function beginFix(card: DoneCard): void {
    if (!ownerMayChange()) return;
    fixingKey = auditItemKey(card.loaded);
    splitOpen = false;
    docIndex = 0;
    conflictText = card.conflict
      ? "This item changed before your fix was posted — please look again"
      : null;
    paint();
  }

  async function runLooksRight(loaded: LoadedItem): Promise<void> {
    if (!ownerMayChange()) return;
    const key = auditItemKey(loaded);
    const blocked = looksRightBlockedText(loaded, key);
    if (blocked !== null) {
      // The Worker's review handler ignores a pending decision and answers
      // 200, so a review posted here would say "cannot be undone here" while
      // the change still applied (R5-03). Say what is in the way instead.
      toast.textContent = blocked;
      return;
    }
    const label = `Looks right for ${payeeText(loaded.item)}`;
    await runPageAction(toast, label, async () => {
      // The review is this item's write and takes its turn, like a Fix and an
      // Undo do: a decision pressed while it is in the air waits for it, so a
      // failed review cannot be restored over the newer card (R5-06).
      const previous = inflight.get(key);
      let finishTurn: (error?: unknown) => void = () => undefined;
      const turn = new Promise<void>((resolve, reject) => {
        finishTurn = (error) => {
          if (error === undefined) resolve();
          else reject(error instanceof Error ? error : new Error("The write for this item failed."));
        };
      });
      turn.catch(() => undefined);
      inflight.set(key, turn);
      let card: DoneCard | null = null;
      let failure: unknown = undefined;
      try {
        if (previous) await previous.catch(() => undefined);
        const wasActive = activeLoaded() === loaded;
        // The card this review belongs to, captured before the move repaints
        // (round-2 review M2): the next card's Undo must stay clickable.
        const writeScope = decisionWriteScope(loaded);
        moveOptimistic(loaded, "Looks right", "review");
        card = sessionDone.find((done) => auditItemKey(done.loaded) === key) ?? null;
        // The next item is what the person is on now; focus follows it instead
        // of falling back to the page body (round nine B9-M1, R5-09).
        if (wasActive) focusAfterPaint();
        const outcome = await runWrite({
          key: `review:${key}`,
          scope: writeScope,
          gate: () => null,
          send: () =>
            opts.client.postExpenseAuditReview(
              loaded.company,
              loaded.item.targetId,
              loaded.item.legsSha256,
            ),
          stillCurrent,
        });
        if (outcome.kind !== "landed") {
          throw outcome.error instanceof Error ? outcome.error : new Error("The review did not land.");
        }
        // A write that landed supersedes the last failed Undo's message.
        undoNotice.textContent = "";
      } catch (error) {
        failure = error;
        restoreCard(card, error);
        throwIfNotConflict(error);
      } finally {
        finishTurn(failure);
        if (inflight.get(key) === turn) inflight.delete(key);
      }
    });
  }

  /** Why Enter may not review this item right now, or null when it may. */
  function looksRightBlockedText(loaded: LoadedItem, key: string): string | null {
    if (inflight.has(key) || (queuedWrites.get(key) ?? 0) > 0) {
      return LOOKS_RIGHT_SAVING_TEXT;
    }
    // The Worker already said this one applied (a 409 cancel). Enter must not
    // read the stale pending_apply snapshot as "waiting for the next update":
    // the change is in the books now (round 2, SHOULD-FIX).
    const appliedHere = sessionDone.some(
      (card) => auditItemKey(card.loaded) === key && card.cannotUndo === true,
    );
    if (appliedHere) return ALREADY_APPLIED_TEXT;
    // A pending decision here is either the Worker's own record or this
    // session's card; both mean a change will still apply after the review.
    const pendingHere =
      effectiveBooking(loaded).pending !== null ||
      sessionDone.some((card) => auditItemKey(card.loaded) === key && card.kind === "decision");
    if (pendingHere) return LOOKS_RIGHT_PENDING_TEXT;
    if (fixingKey === key) return LOOKS_RIGHT_FIX_OPEN_TEXT;
    return null;
  }

  /** Focus follows the work: the card on screen, or the empty state. */
  function focusAfterPaint(): void {
    const card = activeCard();
    if (card !== null) {
      card.focus();
      return;
    }
    body.querySelector<HTMLElement>('[data-role="audit-empty"]')?.focus();
  }

  function activeCard(): HTMLElement | null {
    return body.querySelector<HTMLElement>('[data-role="audit-active"]');
  }

  /**
   * The scope a decision write disables: the card the decision BELONGS to,
   * captured before the optimistic move repaints. After the repaint the live
   * card is the NEXT item's, and disabling it swallowed the Undo that sits on it
   * until the 202 came back (round-2 review M2). When the person has already
   * moved on from this item there is no card of its own on screen, so the write
   * disables the detached sink instead of the page: `runWrite`'s one flight per
   * key, not the disabled attribute, is what keeps one press to one send.
   */
  function decisionWriteScope(loaded: LoadedItem): HTMLElement {
    const card = activeCard();
    return card !== null && activeLoaded() === loaded ? card : writeScopeSink;
  }

  async function runQuickPick(number: number): Promise<void> {
    if (!ownerMayChange()) return;
    const loaded = activeLoaded();
    if (!loaded) return;
    const pick = quickPicksForFilter()[number - 1];
    if (!pick) return;
    const account = resolveQuickPickAccount(pick.suffix, accountsFor(loaded.company));
    if (account === null) return;
    await runRecategorize(loaded, account, pick.name);
  }

  async function runRecategorize(
    loaded: LoadedItem,
    account: string,
    name: string,
  ): Promise<void> {
    if (!ownerMayChange()) return;
    // No legs is not a split: there is nothing to rewrite and nothing to seed
    // an editor with. Say the lines could not be read instead of opening an
    // empty Split (round 2, SHOULD-FIX).
    const booking = effectiveBooking(loaded);
    if (booking.legs.length === 0) {
      toast.textContent = NO_BOOKING_LINES_TEXT;
      return;
    }
    const only = singleBookingLeg(loaded);
    if (only === null) {
      // A quick pick or a Category change rewrites the whole item as ONE line
      // (the Worker checks the total only). On a split, or on a paired item
      // whose stored legs include the Due-From line, that moves money to the
      // first leg's place without saying so (R5-02, R5-05). Split is the way.
      toast.textContent = SPLIT_ONLY_TEXT;
      openSplit();
      return;
    }
    // A pick that names the category already booked changes nothing; posting it
    // would draw "Repairs → Repairs" and a card claiming a change (R5-10).
    if (only.account === account) {
      toast.textContent = `This is already booked as ${auditAccountWords(loaded.company, account)}.`;
      return;
    }
    const leg: ExpenseAuditLeg = {
      company: loaded.company,
      location: only.location,
      account,
      amount: only.amount,
    };
    // The same words as the Category field: two accounts that read the same
    // are told apart, so a real change never reads as "Repairs → Repairs".
    const from = auditAccountWords(loaded.company, only.account);
    const change = `${from} → ${auditAccountWords(loaded.company, account, name)}`;
    await postLines(loaded, [leg], change);
  }

  async function runSplit(loaded: LoadedItem, drafts: readonly SplitLineDraft[]): Promise<void> {
    if (!ownerMayChange()) return;
    const total = effectiveBooking(loaded).total;
    if (total === null) return;
    const resolved = resolvedSplitCents(drafts, total);
    const lines: ExpenseAuditLeg[] = [];
    for (let i = 0; i < drafts.length; i += 1) {
      const draft = drafts[i];
      const cents = resolved[i];
      if (!draft || cents === undefined || cents === null) return;
      lines.push({
        company: draft.company,
        location: draft.location,
        account: draft.account,
        amount: centsToAmount(cents),
      });
    }
    const change = lines
      .map((line) => `${auditAccountWords(line.company, line.account)} ${line.amount}`)
      .join(" · ");
    splitOpen = false;
    await postLines(loaded, lines, change);
  }

  async function postLines(
    loaded: LoadedItem,
    lines: ExpenseAuditLeg[],
    change: string,
  ): Promise<void> {
    const label = `Save for ${payeeText(loaded.item)}`;
    await runPageAction(toast, label, async () => {
      const key = auditItemKey(loaded);
      // Writes for one item go one at a time. This write claims the item's
      // next turn BEFORE it waits, so a second press while it waits queues
      // behind it (and then replaces it) instead of posting beside it with no
      // replaces id. The turn settles the way this write does, so an Undo
      // waiting on it still learns that a cancel-on-arrival was refused.
      const previous = inflight.get(key);
      let finishTurn: (error?: unknown) => void = () => undefined;
      const turn = new Promise<void>((resolve, reject) => {
        finishTurn = (error) => {
          if (error === undefined) resolve();
          else reject(error instanceof Error ? error : new Error("The write for this item failed."));
        };
      });
      turn.catch(() => undefined);
      inflight.set(key, turn);
      // Wait for any write or Undo still in flight for this item, whatever its
      // outcome: a refused cancel puts its decision card back first, so the
      // id this write replaces is read from what the server actually holds.
        if (previous) {
          queuedWrites.set(key, (queuedWrites.get(key) ?? 0) + 1);
          try {
            await previous.catch(() => undefined);
          } finally {
            const left = (queuedWrites.get(key) ?? 1) - 1;
            if (left > 0) queuedWrites.set(key, left);
            else queuedWrites.delete(key);
          }
        }
        const replacesNow = replacesId(loaded);
        // Was the person on this item? A write that waited its turn can land
        // after they moved on, and focus must stay where they are (R5-09).
        const wasActive = activeLoaded() === loaded;
        // The card this decision belongs to, captured before the move repaints:
        // the card that comes on screen next carries the Undo for this one and
        // must stay live while the POST is in the air (round-2 review M2).
        const writeScope = decisionWriteScope(loaded);
        // The Worker stores a no-op when the posted lines equal the booking, and
        // such a card must not promise a change that will not come (R5-08).
        const noop = sameBooking(effectiveBooking(loaded).legs, lines);
        moveOptimistic(loaded, change, "decision", noop, lines);
        // The card this request owns. Undo may take it off the list while the
        // POST is still in the air, so the accepted id is recorded on the card
        // object itself: Undo holds that object, and a refused cancel has to
        // give back a card that can be undone again (round ten, round 3).
        const card = sessionDone.find((done) => auditItemKey(done.loaded) === key) ?? null;
        if (wasActive) focusAfterPaint();
        const body: ExpenseAuditDecisionBody = {
          expected_legs_sha256: loaded.item.legsSha256,
          lines,
        };
        if (replacesNow) body.replaces_decision_id = replacesNow;
        const task = (async () => {
          const accepted: { value: { decisionId: string; icPairId: string | null } | null } = {
            value: null,
          };
          const outcome = await runWrite({
            key: `decision:${key}`,
            scope: writeScope,
            gate: () => null,
            send: () =>
              opts.client.postExpenseAuditDecision(
                loaded.company,
                loaded.item.targetId,
                body,
              ),
            afterLanded: (value) => {
              accepted.value = { decisionId: value.decisionId, icPairId: value.icPairId };
            },
            stillCurrent,
          });
          if (outcome.kind !== "landed") {
            // Nothing landed, so a decision that lands later is not cancelled.
            cancelOnArrival.delete(key);
            restoreCard(card, outcome.error);
            throwIfNotConflict(outcome.error);
            return;
          }
          const landed = accepted.value;
          if (landed === null) return;
          if (card !== null) {
            card.decisionId = landed.decisionId;
            // A pair id the Worker minted is part of the live decision: the
            // next quick pick must see it, or it would dissolve the pair the
            // card says is there (R5-05, round 3).
            card.icPairId = landed.icPairId;
          }
          // A write that landed supersedes the last failed Undo's message.
          undoNotice.textContent = "";
          if (!cancelOnArrival.has(key)) return;
          cancelOnArrival.delete(key);
          // The cancel is its own write, so it keeps its own gate. Its refusal
          // rejects this item's turn, so Undo learns the server still holds the
          // decision and can put the card back with its accepted id.
          const cancelOutcome = await runWrite({
            key: `cancel-on-arrival:${key}`,
            scope: opts.root,
            gate: () => null,
            send: () =>
              opts.client.cancelExpenseAuditDecision(loaded.company, landed.decisionId),
            stillCurrent,
          });
          if (cancelOutcome.kind !== "landed") {
            throw cancelOutcome.error instanceof Error ? cancelOutcome.error : new Error("The cancel did not land.");
          }
        })();
      // The try holds only the wait for the turn, so a re-read or an await
      // around the write can never be reported as "did not save" (A5-1).
      let failure: unknown = undefined;
      try {
        await task;
      } catch (error) {
        failure = error;
      }
      finishTurn(failure);
      if (inflight.get(key) === turn) inflight.delete(key);
      if (failure !== undefined) throw failure instanceof Error ? failure : new Error("The write for this item failed.");
    });
  }

  async function runUndo(): Promise<void> {
    if (!ownerMayChange()) return;
    const card = sessionDone[0];
    if (!card) return;
    if (card.kind === "review") {
      // Nothing is sent and nothing moves: the server keeps the item reviewed.
      toast.textContent = "Looks right cannot be undone here. Use Fix to change the category.";
      return;
    }
    if (card.cannotUndo === true) {
      // The Worker already said this one applied; nothing can cancel it.
      undoNotice.textContent = ALREADY_APPLIED_TEXT;
      return;
    }
    // A newer write for this item is still waiting its turn: the card on top
    // is not the latest change yet, so an Undo now would cancel the wrong one.
    if ((queuedWrites.get(auditItemKey(card.loaded)) ?? 0) > 0) {
      toast.textContent = "Still saving this item. Undo when the save has finished.";
      return;
    }
    // Round nine A9-M4: a decision that replaced a "Looks right" card is undone
    // back to that REVIEW card. The Worker keeps the review row when the legs
    // are unchanged, so putting the item back on the undecided pile made the
    // page claim an item was open while the server still held it reviewed (and
    // the "to go" count went up for work already done).
    //
    // Round ten MUST-FIX 1: the screen repaints before the cancel is sent, so a
    // refused cancel must not leave a card claiming a state the server does not
    // hold. Round ten, round 3: the rollback reverses Undo's own change only —
    // whatever else happened while the cancel was in flight stays as it is.
    const restoresReview = card.replacedReview;
    const key = auditItemKey(card.loaded);
    // Undo settles this session's pending decision: the next read must not
    // offer it again from the stale load-time item (B4a-2).
    cancelledDecisions.add(card.decisionId ?? key);
    const label = `Undo for ${payeeText(card.loaded.item)}`;
    if (restoresReview) {
      sessionDone = [
        {
          loaded: card.loaded,
          changeLabel: "Looks right",
          decisionId: null,
          kind: "review",
          conflict: false,
          replacedReview: false,
        },
        ...sessionDone.slice(1),
      ];
    } else {
      sessionDone = sessionDone.slice(1);
      undecided.splice(index, 0, card.loaded);
    }
    fixingKey = null;
    splitOpen = false;
    conflictText = null;
    undoNotice.textContent = "";
    paint();
    focusAfterPaint();
    // The Undo is itself a write in flight for this item: a Fix pressed before
    // the cancel answers waits for it (postLines), so it replaces the decision
    // the server really holds. Without this a refused cancel restored the old
    // card over the newer one, and the newer decision could never be undone.
    const pending = inflight.get(key);
    const task = (async (): Promise<void> => {
      let refusal: unknown = undefined;
      const undone = await runPageAction(toast, label, async () => {
        if (card.kind !== "decision") return;
        if (card.decisionId === null) {
          cancelOnArrival.add(key);
          if (pending) await pending;
          return;
        }
        const outcome = await runWrite({
          key: `undo:${key}`,
          scope: opts.root,
          gate: () => null,
          send: () =>
            opts.client.cancelExpenseAuditDecision(card.loaded.company, card.decisionId ?? ""),
          stillCurrent,
        });
        if (outcome.kind !== "landed") {
          refusal = outcome.error;
          throw outcome.error instanceof Error ? outcome.error : new Error("The undo did not land.");
        }
      });
      if (undone || !stillCurrent()) return;
      if (refusal instanceof AdminApiError && refusal.status === 409) {
        // The pipeline already applied it: the Worker's cancel answers 409 for
        // every retry, so "Try again" would be a dead end (R5-08).
        markAlreadyApplied(card);
        return;
      }
      if (card.decisionId === null) {
        // Nothing the server holds: the post this Undo was waiting on never
        // posted, and its own rollback already put the item back.
        return;
      }
      rollbackUndo(card, refusal);
    })();
    inflight.set(key, task);
    try {
      await task;
    } finally {
      if (inflight.get(key) === task) inflight.delete(key);
    }
  }

  /**
   * Puts back the card Undo took off the list, and nothing else (round ten,
   * round 3 MUST-FIX 2). The lists are edited where they stand: a card another
   * action added, or a page that `loadMore` appended while the cancel was in
   * flight, is left alone — a captured snapshot used to overwrite both, and a
   * dropped page could never be asked for again because its cursor had moved.
   */
  function rollbackUndo(card: DoneCard, error: unknown): void {
    const key = auditItemKey(card.loaded);
    // The cancel was refused, so the decision is still the server's: let the
    // next read offer it again.
    cancelledDecisions.delete(card.decisionId ?? key);
    // The person stays on the item they are looking at: the index is found
    // again by key, not left pointing at whatever slid into its place.
    const showing = undecided[index];
    const showingKey = showing === undefined ? null : auditItemKey(showing);
    sessionDone = [card, ...sessionDone.filter((row) => auditItemKey(row.loaded) !== key)];
    undecided = undecided.filter((row) => auditItemKey(row) !== key);
    const stillThere =
      showingKey === null ? -1 : undecided.findIndex((row) => auditItemKey(row) === showingKey);
    if (stillThere >= 0) index = stillThere;
    keepIndexInRange();
    fixingKey = null;
    splitOpen = false;
    conflictText = null;
    undoNotice.textContent = failureCopy(error, { role, context: "write" }).sentence;
    paint();
  }

  /**
   * Puts an already-applied decision's card back without an Undo. The change is
   * in the books, so the item is not offered again and the shared toast's
   * "failed" line is cleared rather than left contradicting the notice.
   */
  function markAlreadyApplied(card: DoneCard): void {
    const key = auditItemKey(card.loaded);
    const restored: DoneCard = { ...card, cannotUndo: true, conflict: false };
    sessionDone = [restored, ...sessionDone.filter((row) => auditItemKey(row.loaded) !== key)];
    undecided = undecided.filter((row) => auditItemKey(row) !== key);
    keepIndexInRange();
    fixingKey = null;
    splitOpen = false;
    conflictText = null;
    toast.textContent = "";
    undoNotice.textContent = ALREADY_APPLIED_TEXT;
    paint();
  }

  /** Keeps the active position inside the loaded items after a list change. */
  function keepIndexInRange(): void {
    if (index >= undecided.length) index = Math.max(0, undecided.length - 1);
  }

  function moveOptimistic(
    loaded: LoadedItem,
    change: string,
    kind: DoneCard["kind"],
    sameBookingNow = false,
    lines?: readonly ExpenseAuditLeg[],
  ): void {
    const key = auditItemKey(loaded);
    const previous = sessionDone.find((row) => auditItemKey(row.loaded) === key);
    const next: DoneCard = {
      loaded,
      changeLabel: change,
      decisionId: null,
      kind,
      conflict: false,
      // A failed write gives this card back (B4a-2): keep the card it replaced
      // so the older state is not lost.
      ...(previous !== undefined ? { previous } : {}),
      // The booking this decision will leave behind. A later quick pick reads
      // it (through `effectiveBooking`) instead of the stale load-time legs
      // (round 3, HIGH).
      ...(lines !== undefined ? { lines } : {}),
      ...(sameBookingNow ? { sameBooking: true } : {}),
      // A decision can replace a decision (Looks right, Fix, Fix). The review
      // under it is still what the server holds, so it is carried forward, not
      // re-derived from the card immediately replaced (round ten MUST-FIX 3).
      replacedReview: previous?.kind === "review" || previous?.replacedReview === true,
    };
    if (fixingKey === key) {
      sessionDone = sessionDone.map((card) => (auditItemKey(card.loaded) === key ? next : card));
      fixingKey = null;
    } else {
      // A write that waited its turn can land after the person moved on: they
      // stay on the item they are looking at. When it is this item, the next
      // one slides into its place as before.
      const showing = undecided[index];
      const showingKey = showing === undefined ? null : auditItemKey(showing);
      undecided = undecided.filter((row) => auditItemKey(row) !== key);
      sessionDone = [next, ...sessionDone.filter((card) => auditItemKey(card.loaded) !== key)];
      if (showingKey !== null && showingKey !== key) {
        const stillThere = undecided.findIndex((row) => auditItemKey(row) === showingKey);
        if (stillThere >= 0) index = stillThere;
      }
      keepIndexInRange();
    }
    splitOpen = false;
    docIndex = 0;
    conflictText = null;
    paint();
  }

  /**
   * Takes back THIS write's card and nothing else (R5-06). A newer write for
   * the same item has already replaced the card object, so the failed write
   * leaves it alone: the Fix the server accepted survives.
   */
  function restoreCard(card: DoneCard | null, error: unknown): void {
    if (card === null) return;
    if (!sessionDone.includes(card)) return;
    const key = auditItemKey(card.loaded);
    if (error instanceof AdminApiError && error.status === 409) {
      card.conflict = true;
      conflictText = "This item changed before your fix was posted — please look again";
      sessionDone = sessionDone.filter((done) => auditItemKey(done.loaded) !== key);
      undecided = [card.loaded, ...undecided.filter((row) => auditItemKey(row) !== key)];
      index = 0;
      fixingKey = null;
      paint();
      return;
    }
    sessionDone = sessionDone.filter((done) => auditItemKey(done.loaded) !== key);
    if (card.previous !== undefined) {
      // A failed Fix leaves the older card where the person left it; putting
      // the item back on the undecided pile would claim the older work never
      // happened (B4a-2).
      sessionDone = [card.previous, ...sessionDone];
      fixingKey = null;
      paint();
      return;
    }
    undecided = [card.loaded, ...undecided.filter((row) => auditItemKey(row) !== key)];
    index = 0;
    fixingKey = null;
    paint();
  }

  function throwIfNotConflict(error: unknown): void {
    if (error instanceof AdminApiError && error.status === 409) return;
    throw error;
  }

  function replacesId(loaded: LoadedItem): string | undefined {
    const key = auditItemKey(loaded);
    // The Worker's latest decision is the row a new one replaces, whatever its
    // status: an applied decision is another reclass, and a pending one is the
    // change still waiting to apply (round 3, HIGH).
    const latestId = effectiveBooking(loaded).decision?.id ?? undefined;
    if (fixingKey !== key) {
      const existing = sessionDone.find((card) => auditItemKey(card.loaded) === key);
      if (!existing) return latestId;
      return existing.decisionId ?? undefined;
    }
    const card = sessionDone.find((done) => auditItemKey(done.loaded) === key);
    return card?.decisionId ?? latestId;
  }

  function maybeLoadMore(): void {
    if (undecided.length - index > 3) return;
    void loadMore();
  }

  async function loadMore(): Promise<void> {
    // The filter this page extends: a reload that started while the page was
    // in flight replaces it, and a page from the old filter is dropped.
    const seq = readSeq;
    try {
      let added = false;
      for (const [company, cursor] of cursors) {
        if (!cursor) continue;
        const page = await fetchPage(company, cursor);
        if (!stillCurrent() || seq !== readSeq) return;
        cursors.set(company, page.nextAfter);
        // An item that already has a done card is not offered again in the
        // "Not reviewed" pile, however the server still classes it (R5-03).
        // The other filters list those items on purpose.
        const doneKeys =
          filter.status === "not_reviewed"
            ? new Set(sessionDone.map((card) => auditItemKey(card.loaded)))
            : new Set<string>();
        for (const item of page.items) {
          const loaded = { company, item };
          if (matchesClientFilter(loaded) && !doneKeys.has(auditItemKey(loaded))) {
            undecided.push(loaded);
            added = true;
          }
        }
      }
      if (added) paint();
    } catch (error) {
      if (!stillCurrent()) return;
      toast.textContent = `Load more failed: ${loadErrorText(error)}`;
    }
  }

  function prefetchOnce(again = false): void {
    if (prefetchTask !== null) {
      // A Retry must not be swallowed by a pass that already left this
      // document behind: run one more pass when the current one ends.
      if (again) prefetchAgain = true;
      return;
    }
    const task = prefetch();
    prefetchTask = task;
    void task.finally(() => {
      if (prefetchTask === task) prefetchTask = null;
      if (prefetchAgain) {
        prefetchAgain = false;
        prefetchOnce();
      }
    });
  }

  async function prefetch(): Promise<void> {
    // mintMissing records every per-document failure itself; the net keeps an
    // unexpected throw from leaving the panel on "Loading preview…".
    const changed = await mintMissing().catch(() => true);
    if (!stillCurrent()) return;
    if (changed) draw();
    opts.root.setAttribute("data-prefetch-ready", "true");
  }

  /**
   * Mints the links the visible documents need. Each document is asked for
   * once per pass; a link that comes back stale is asked for again (the server
   * can hand back a nearly expired URL), but a document the server did not
   * name is not asked twice — it is reported as unavailable instead.
   */
  async function mintMissing(): Promise<boolean> {
    // Document links are the owner's (the Worker refuses anyone else).
    if (!ownerMayChange()) return false;
    let changed = false;
    const asked = new Set<string>();
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const byCompany = new Map<string, { documentId: string; key: string }[]>();
      const windowItems = [activeLoaded(), ...undecided.slice(index, index + 3)].filter(
        (row): row is LoadedItem => row !== null,
      );
      for (const loaded of windowItems) {
        for (const doc of loaded.item.documents) {
          const key = previewKey(slugText(loaded.company), doc.documentId);
          const cached = urlCache.get(key);
          if (cached && urlFresh(cached.expiresAt)) continue;
          if (!cached && asked.has(key)) continue;
          asked.add(key);
          const list = byCompany.get(loaded.company) ?? [];
          if (!list.some((row) => row.documentId === doc.documentId)) {
            list.push({ documentId: doc.documentId, key });
          }
          byCompany.set(loaded.company, list);
        }
      }
      if (byCompany.size === 0) return changed;
      for (const [company, wanted] of byCompany) {
        for (let start = 0; start < wanted.length; start += 20) {
          const slice = wanted.slice(start, start + 20);
          const ids = slice.map((row) => row.documentId);
          const urls: {
            value: Awaited<ReturnType<AdminClient["mintExpenseAuditDocumentUrls"]>> | null;
          } = { value: null };
          const outcome = await runWrite({
            key: `document-links:${company}:${ids.join(",")}`,
            scope: backgroundScope,
            gate: () => null,
            send: () => opts.client.mintExpenseAuditDocumentUrls(company, ids),
            afterLanded: (value) => {
              urls.value = value;
            },
            stillCurrent,
          });
          if (outcome.kind !== "landed") {
            for (const row of slice) {
              previewFailures.set(row.key, previewFailureDetail(outcome.error));
              changed = true;
            }
            continue;
          }
          if (!stillCurrent() || urls.value === null) return changed;
          const returned = new Set<string>(urls.value.map((signed) => signed.documentId));
          for (const signed of urls.value) {
            urlCache.set(previewKey(company, signed.documentId), signed);
            changed = true;
          }
          for (const row of slice) {
            if (returned.has(row.documentId)) {
              previewFailures.delete(row.key);
            } else {
              previewFailures.set(row.key, "The document link was not returned.");
              changed = true;
            }
          }
        }
      }
    }
    return changed;
  }

  function matchesClientFilter(loaded: LoadedItem): boolean {
    const item = loaded.item;
    if (filter.status === "not_reviewed" && item.reviewState !== "not_reviewed") return false;
    if (filter.status === "changed" && item.reviewState !== "changed") return false;
    if (filter.status === "reviewed" && item.reviewState !== "reviewed") return false;
    if (filter.source && item.source !== filter.source) return false;
    const booking = effectiveBooking(loaded);
    if (filter.location) {
      const hit = booking.legs.some((leg) => leg.location === filter.location);
      if (!hit) return false;
    }
    const viewId = effectiveViewId();
    const view = viewChoices().find((row) => row.id === viewId);
    if (!view || viewId === "all") return true;
    if (view.accountSuffixes.length === 0) return true;
    return booking.legs.some((leg) =>
      view.accountSuffixes.some((suffix) => accountMatchesSuffix(accountText(leg.account), suffix)),
    );
  }

  /**
   * The category group's NAME, which is the key the Worker's summary uses. An
   * id it cannot name has no server-backed count (never "all": that would be a
   * number from a wider scope).
   */
  function currentViewName(): string {
    const viewId = effectiveViewId();
    if (viewId === "all") return "all";
    return viewChoices().find((row) => row.id === viewId)?.name ?? "";
  }

  /**
   * The category group the filter is really on (rd13 L1). The built-in
   * Maintenance group is not offered when the company has its own group of
   * that name, so a filter still holding the built-in id means that group —
   * never "no group at all", which would list every company's cards.
   */
  function effectiveViewId(): string {
    if (filter.viewId !== LOCAL_MAINTENANCE_ID) return filter.viewId;
    const own = ownMaintenanceView();
    return own?.id ?? LOCAL_MAINTENANCE_ID;
  }

  /** The company's own Maintenance group, when it has one. */
  function ownMaintenanceView(): ExpenseAuditViewRecord | undefined {
    const local = readViewDetails();
    return [...views, ...Object.values(local)].find(
      (view) => view.name.trim().toLowerCase() === MAINTENANCE_NAME,
    );
  }

  /**
   * The category groups the picker offers.
   *
   * rd13 L1: the built-in Maintenance group is only the default when the
   * company has no group of that name. A company that saved its own
   * "Maintenance" group used to see the category twice — the built-in copy and
   * the real one — which reads as two categories where there is one.
   */
  function viewChoices(): ExpenseAuditViewRecord[] {
    const local = readViewDetails();
    const merged = new Map<string, ExpenseAuditViewRecord>();
    const ownMaintenance = ownMaintenanceView() !== undefined;
    if (!ownMaintenance) {
      merged.set(LOCAL_MAINTENANCE_ID, {
        id: LOCAL_MAINTENANCE_ID,
        name: "Maintenance",
        accountSuffixes: [...DEFAULT_MAINTENANCE_SUFFIXES],
        quickPicks: DEFAULT_QUICK_PICKS.map((pick) => pick.suffix),
      });
    }
    for (const view of views) {
      const stored = local[view.id];
      const maintenance = view.name.trim().toLowerCase() === MAINTENANCE_NAME;
      merged.set(view.id, {
        id: view.id,
        name: view.name,
        accountSuffixes:
          view.accountSuffixes.length > 0
            ? view.accountSuffixes
            : stored?.accountSuffixes ??
              (maintenance ? [...DEFAULT_MAINTENANCE_SUFFIXES] : []),
        quickPicks:
          view.quickPicks.length > 0
            ? view.quickPicks
            : stored?.quickPicks ??
              (maintenance ? DEFAULT_QUICK_PICKS.map((pick) => pick.suffix) : []),
      });
    }
    for (const [id, stored] of Object.entries(local)) {
      if (!merged.has(id)) merged.set(id, stored);
    }
    return [...merged.values()];
  }

  function quickPicksForFilter(): ({ name: string; suffix: string } | null)[] {
    const view = viewChoices().find((row) => row.id === filter.viewId);
    const suffixes =
      view && view.quickPicks.length > 0
        ? view.quickPicks
        : DEFAULT_QUICK_PICKS.map((pick) => pick.suffix);
    const picks: ({ name: string; suffix: string } | null)[] = [];
    for (let i = 0; i < 9; i += 1) {
      const suffix = suffixes[i];
      if (!suffix) {
        picks.push(null);
        continue;
      }
      const known = DEFAULT_QUICK_PICKS.find((pick) => pick.suffix === suffix);
      picks.push({ name: known?.name ?? leaf(suffix, labels), suffix });
    }
    return picks;
  }

  function accountsFor(company: CompanySlug): string[] {
    const snapshot = charts.get(company);
    if (!snapshot) return [];
    return snapshot.selectableAccounts.filter((account) => auditAccountAllowed(account));
  }

  /**
   * An account as the audit page words it: its last segment under a heading,
   * unless another account of the company reads the same, and then the whole
   * label the Category field uses (`Expenses › Repairs (RENT)`). `plain` is the
   * wording a unique account keeps (a quick pick's own name, say).
   */
  function auditAccountWords(
    company: CompanySlug,
    account: AccountPath,
    plain: string = leaf(account, labels),
  ): string {
    return companyAccountLabels(() => accountsFor(company), labels).whenShared(company, account) ?? plain;
  }

  function locationsFor(company: CompanySlug): string[] {
    const snapshot = charts.get(company);
    if (!snapshot) return [];
    return snapshot.selectableLocations.length > 0
      ? snapshot.selectableLocations
      : snapshot.locations;
  }

  /**
   * The companies a split of `itemCompany`'s line may name, built from the
   * companies this console loaded and worded by the one directory (rd r12 F1,
   * R5: the picker offered a hard-coded list of MJN slugs, so in any other
   * console it was empty).
   *
   * The Worker's own rule bounds the list (`expense_audit.ts`, the decision
   * route): a line may always stay on the item's own company, and may name
   * another company only when both are in its IC split set; a medical company
   * is refused either way. Offering more would let the person build a split
   * the Worker answers with 422. `IC_SPLIT_COMPANIES` is that set's copy here.
   */
  function splitCompanies(itemCompany: CompanySlug): { slug: CompanySlug; label: string }[] {
    const icItem = IC_SPLIT_SET.has(itemCompany);
    return opts.companies
      .filter((row) => !isMedicalSlug(row.slug))
      .filter((row) => row.slug === itemCompany || (icItem && IC_SPLIT_SET.has(row.slug)))
      .map((row) => ({
        slug: row.slug,
        label: face.company(row.slug, directory, "sentence"),
      }));
  }

  function isMedicalSlug(slug: CompanySlug): boolean {
    // Fail closed: a slug the company list does not (yet) carry is treated as
    // medical and left out, so a partly loaded list never offers it as a target.
    const company = opts.companies.find((row) => row.slug === slug);
    return company === undefined || company.featurePacks.includes("medical");
  }

  function showAddressFilter(): boolean {
    const companies = selectedCompanies();
    return companies.length === 1 && companies[0]?.slug === "sor-properties";
  }

  /**
   * "N to go" is exact when the server's unreviewed count backs it, or when
   * every page of items has been read. Otherwise it is the items loaded so far,
   * and more pages remain, so it says "or more".
   */
  function toGoText(): string {
    const backedByServer = summaryUnreviewed !== null && filter.status === "not_reviewed";
    const everyPageRead = [...cursors.values()].every((cursor) => !cursor);
    return countOrMoreText(toGo(), backedByServer || everyPageRead);
  }

  function toGo(): number {
    const decided = sessionDone.length;
    if (summaryUnreviewed !== null && filter.status === "not_reviewed") {
      return Math.max(0, summaryUnreviewed - decided);
    }
    return undecided.length;
  }

  function splitSeed(loaded: LoadedItem): SplitLineDraft[] {
    const legs = effectiveBooking(loaded).legs;
    if (legs.length === 0) {
      lineSeq += 1;
      return [
        {
          id: `split-${String(lineSeq)}`,
          company: slugText(loaded.company),
          location: "",
          account: "",
          amountText: "",
        },
      ];
    }
    return legs.map((leg) => {
      lineSeq += 1;
      // The split editor's own type carries the path as a plain string; the
      // booking's company only seeds which company the line posts to.
      const lineCompany = slugText(leg.company || loaded.company);
      return {
        id: `split-${String(lineSeq)}`,
        company: lineCompany,
        location: leg.location,
        account: leg.account,
        amountText: leg.amount,
      };
    });
  }

  /**
   * The one leg a quick pick may rewrite, or null when a single line would not
   * be the same booking: more than one leg (a split, or a paired item whose
   * stored legs include the Due-From line), a leg another company owns, or a
   * decision still waiting to apply whose booking is not exactly one line or
   * carries an intercompany pair id. Such a pending pair can still show one leg
   * locally, and a pending split shows whatever the load-time page happened to
   * hold, so the decision is read from the LIVE booking — this session's card
   * when there is one, the Worker's `latestDecision` otherwise (R5-02, R5-05,
   * round 3).
   * A settled decision is another reclass; a single-leg item with no pair keeps
   * its own location and amount, so the one-keystroke recategorise stays.
   */
  function singleBookingLeg(loaded: LoadedItem): ExpenseAuditLeg | null {
    const booking = effectiveBooking(loaded);
    const pending = booking.pending;
    if (pending !== null && (pending.lineCount !== 1 || pending.icPairId !== null)) return null;
    const legs = booking.legs;
    if (legs.length !== 1) return null;
    const only = legs[0];
    if (!only) return null;
    if (only.company !== "" && only.company !== loaded.company) return null;
    return only;
  }

  /**
   * The ONE booking this page decides over (rd round 12, B4a-2).
   *
   * Priority: this session's card (an applied card has no pending change) ▸
   * the Worker's pending `latestDecision` ▸ `currentLegs`. Every reader on this
   * page goes through here, so a quick pick, the Split editor, the "Booked as"
   * lines and the changed-this-before checks cannot disagree about what is
   * booked. `rd-r12-b4a-effective-booking-guard.test.ts` fails any read of
   * `.currentLegs` or `.latestDecision` outside this function.
   */
  function effectiveBooking(loaded: LoadedItem): EffectiveBooking {
    const decision = loaded.item.latestDecision;
    const card = liveDecisionCard(loaded);
    if (card !== undefined) {
      // An applied card is another reclass with no change still waiting.
      const applied = card.cannotUndo === true;
      const legs = card.lines ?? loaded.item.currentLegs;
      return {
        legs,
        total: legsTotal(legs),
        pending: applied
          ? null
          : {
              lineCount: card.lines?.length ?? 0,
              icPairId: card.icPairId ?? null,
              decisionId: card.decisionId,
              pairState: decision?.pairState ?? "none",
              statusReason: decision?.statusReason ?? null,
            },
        source: "session",
        decision,
      };
    }
    if (
      decision !== null &&
      decision.status === "pending_apply" &&
      !cancelledDecisions.has(decision.id) &&
      !cancelledDecisions.has(auditItemKey(loaded))
    ) {
      // A pending decision the Worker sent no lines for cannot describe a
      // booking: show what is booked rather than an empty page (the pending
      // block still refuses a single-line rewrite by its line count).
      const pendingLegs =
        decision.newLegs.length > 0 ? decision.newLegs : loaded.item.currentLegs;
      return {
        legs: pendingLegs,
        total: legsTotal(pendingLegs),
        pending: {
          lineCount: decision.newLegs.length,
          icPairId: decision.icPairId,
          decisionId: decision.id,
          pairState: decision.pairState,
          statusReason: decision.statusReason,
        },
        source: "worker",
        decision,
      };
    }
    return {
      legs: loaded.item.currentLegs,
      total: legsTotal(loaded.item.currentLegs),
      pending: null,
      source: "loaded",
      decision,
    };
  }

  /**
   * A Worker-originated pending PAIRED decision cannot be opened in the Split
   * editor: after a reload its payload carries only the primary row and no
   * company, so the editor would invent the rest of the pair (rc-2 §5 case C).
   * Round-11 decision 5's contract change is deferred; the stopgap says so.
   *
   * A real pair's primary row is the payer's own lines PLUS one Due-From leg per
   * counterpart (ingest-worker/src/expense_audit.ts:819-856), so it has two or
   * more legs. The row count is no signal at all (round-2 review H1); what
   * makes it a pair is the Worker's own record: `icPairId` is minted only when a
   * foreign company is involved, and the pair state says paired/awaiting. A
   * single-company split has neither, so it still opens the editor.
   */
  function pairedSplitCannotOpen(booking: EffectiveBooking): boolean {
    if (booking.source !== "worker" || booking.decision === null) return false;
    return (
      booking.decision.icPairId !== null ||
      booking.decision.pairState === "paired" ||
      booking.decision.pairState === "awaiting_counterpart"
    );
  }

  function liveDecisionCard(loaded: LoadedItem): DoneCard | undefined {
    const key = auditItemKey(loaded);
    return sessionDone.find((done) => auditItemKey(done.loaded) === key && done.kind === "decision");
  }

  /**
   * The company rows this page already holds are the honest source of names;
   * the shell's map is a fallback and a label is never a slug (rd Y1 supplies
   * display names, and `companyNameText` refuses any key that is not one).
   */
  function labelFor(slug: CompanySlug): string {
    return face.company(slug, directory, "sentence");
  }

  function rememberFilters(): void {
    writeJson(FILTER_KEY, filter);
  }

  function rememberView(view: ExpenseAuditViewRecord): void {
    const all = readViewDetails();
    all[view.id] = view;
    writeJson(VIEW_KEY, all);
  }
}

function textLines(value: string): string[] {
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

function legsTotal(legs: readonly ExpenseAuditLeg[]): bigint | null {
  if (legs.length === 0) return null;
  let sum = 0n;
  for (const leg of legs) {
    const cents = decimalToCents(leg.amount);
    if (cents === null) return null;
    sum += cents;
  }
  return sum;
}

/** The posted lines are exactly the stored ones: the Worker stores a no-op. */
function sameBooking(
  current: readonly ExpenseAuditLeg[],
  lines: readonly ExpenseAuditLeg[],
): boolean {
  if (current.length === 0 || current.length !== lines.length) return false;
  const keyOf = (leg: ExpenseAuditLeg): string => {
    const cents = decimalToCents(leg.amount);
    return JSON.stringify([leg.account, leg.location, cents === null ? leg.amount : cents.toString()]);
  };
  const before = current.map(keyOf).sort();
  const after = lines.map(keyOf).sort();
  return before.every((value, index) => value === after[index]);
}

function singleAccount(legs: readonly ExpenseAuditLeg[]): string | null {
  const first = legs[0];
  if (!first) return null;
  if (legs.every((leg) => leg.account === first.account)) return accountText(first.account);
  return null;
}

/** The last segment as words. One helper (rd Y0), never a hand-rolled split. */
function leaf(account: AccountPath, companies: AccountLabelContext): string {
  return accountLeafLabel(account, companies);
}

function pairBadgeText(
  booking: EffectiveBooking,
): { face: string; detail: string | null } | null {
  const decision = booking.decision;
  if (!decision) return null;
  if (decision.pairState === "awaiting_counterpart") {
    return {
      face: "Waiting for the other company. It will show after the next update.",
      detail: "Awaiting counterpart — posts on its next sync",
    };
  }
  if (decision.pairState === "failed_counterpart") {
    return {
      face: "The other company did not accept this.",
      detail: decision.statusReason
        ? `Counterpart failed (${amVisibleDisplayText(decision.statusReason)})`
        : "Counterpart failed",
    };
  }
  if (decision.pairState === "paired" || decision.icPairId) {
    return { face: "Paired", detail: null };
  }
  return null;
}

/** The payee as shown: untrusted bank or hub text, hazards stripped. */
function payeeText(item: ExpenseAuditItem): string {
  // A null payee is a real state (the D1 column is nullable), not unreadable
  // data: it gets a neutral label and the rest of the item still loads.
  return item.payee === null ? "No payee" : amVisibleDisplayText(item.payee);
}

function formatMoney(amount: string): string {
  return moneyText(amount, { fallback: "No amount" });
}

function formatAuditDate(iso: string): string {
  const shown = dateText(iso);
  return shown === "" ? "Unknown date" : shown;
}

function formatMonthYear(iso: string): string {
  const shown = monthText(iso);
  return shown === "" ? "Unknown month" : shown;
}

function monthStart(month: string): string | null {
  return /^\d{4}-\d{2}$/.test(month) ? `${month}-01` : null;
}

function monthEnd(month: string): string | null {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) return null;
  const year = Number(match[1]);
  const mon = Number(match[2]);
  const last = new Date(Date.UTC(year, mon, 0)).getUTCDate();
  return `${month}-${String(last).padStart(2, "0")}`;
}

function urlFresh(expiresAt: string): boolean {
  const exp = Date.parse(expiresAt);
  return Number.isFinite(exp) && exp > Date.now() + 5000;
}

function safeSignedUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    // A signed document URL carries a token: never accept it over plain HTTP.
    if (parsed.protocol !== "https:") return null;
    return parsed.href;
  } catch {
    return null;
  }
}

/** The cache/failure key for one document of one company. */
function previewKey(companyText: string, documentId: string): string {
  return `${companyText}:${documentId}`;
}

/**
 * The words behind "Show details" when a document link could not be had. The
 * face is always "Preview unavailable."; nothing here mentions the audit list,
 * which is a different read and may have loaded perfectly (round nine B9-M1).
 */
function previewFailureDetail(error: unknown): string {
  if (error instanceof AdminApiError && error.status === 403) {
    return "The document link was refused.";
  }
  return "The preview could not be loaded.";
}

function pdfSrc(url: string): string {
  if (url.includes("#")) return url;
  return `${url}#view=FitH`;
}

function unreviewedCount(
  body: unknown,
  slugs: readonly string[],
  viewName: string,
): number | null {
  if (!isRecord(body) || !Array.isArray(body.companies)) return null;
  const want = new Set(slugs);
  let sum = 0;
  let saw = false;
  for (const row of body.companies) {
    if (!isRecord(row)) continue;
    const company = row.company;
    if (typeof company !== "string" || !want.has(company)) continue;
    // The Worker's summary carries `unreviewed_by_view: [{view, count}]`, one
    // entry per category group plus "all". A view it does not name has no
    // server-backed count: falling back to "all" would print a number from a
    // wider scope. `unreviewed` is the portal's older alias until R9.
    const count =
      countForView(row["unreviewed_by_view"] ?? row["unreviewedByView"], viewName) ??
      row["unreviewed"];
    if (typeof count === "number" && Number.isInteger(count) && count >= 0) {
      sum += count;
      saw = true;
    }
  }
  return saw ? sum : null;
}

function countForView(list: unknown, viewName: string): number | null {
  if (!Array.isArray(list) || viewName.length === 0) return null;
  for (const entry of list) {
    if (!isRecord(entry)) continue;
    if (entry["view"] !== viewName) continue;
    const count = entry["count"];
    if (typeof count === "number" && Number.isInteger(count) && count >= 0) return count;
    return null;
  }
  return null;
}

function mergeViews(rows: readonly ExpenseAuditViewRecord[]): ExpenseAuditViewRecord[] {
  const local = readViewDetails();
  return rows.map((row) => {
    const stored = local[row.id];
    return {
      ...row,
      accountSuffixes: row.accountSuffixes.length > 0 ? row.accountSuffixes : stored?.accountSuffixes ?? [],
      quickPicks: row.quickPicks.length > 0 ? row.quickPicks : stored?.quickPicks ?? [],
    };
  });
}

function loadErrorText(error: unknown): string {
  if (error instanceof AdminApiError) {
    if (error.status === 501) return "Audit is not available yet.";
    if (error.status === 403) return "This screen is for the owner.";
    return "The audit list could not be loaded.";
  }
  return "The audit list could not be loaded.";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function readFilters(): StoredFilters {
  const parsed = readJson(FILTER_KEY);
  if (!isRecord(parsed)) return {};
  const out: StoredFilters = {};
  if (typeof parsed.company === "string") out.company = parsed.company;
  if (typeof parsed.from === "string") out.from = parsed.from;
  if (typeof parsed.to === "string") out.to = parsed.to;
  if (typeof parsed.viewId === "string") out.viewId = parsed.viewId;
  if (typeof parsed.status === "string") out.status = parsed.status;
  if (typeof parsed.source === "string") out.source = parsed.source;
  if (typeof parsed.location === "string") out.location = parsed.location;
  return out;
}

function readHideStrip(): boolean {
  try {
    return localStorage.getItem(STRIP_KEY) === "1";
  } catch {
    return false;
  }
}

function writeHideStrip(hidden: boolean): void {
  try {
    localStorage.setItem(STRIP_KEY, hidden ? "1" : "0");
  } catch {
    // remembered per browser; a blocked store still leaves the strip visible
  }
}

function readViewDetails(): Record<string, ExpenseAuditViewRecord> {
  const parsed = readJson(VIEW_KEY);
  if (!isRecord(parsed)) return {};
  const out: Record<string, ExpenseAuditViewRecord> = {};
  for (const [id, value] of Object.entries(parsed)) {
    if (!isRecord(value)) continue;
    if (typeof value.name !== "string") continue;
    const suffixes = Array.isArray(value.accountSuffixes)
      ? value.accountSuffixes.filter((item): item is string => typeof item === "string")
      : [];
    const picks = Array.isArray(value.quickPicks)
      ? value.quickPicks.filter((item): item is string => typeof item === "string")
      : [];
    out[id] = { id, name: value.name, accountSuffixes: suffixes, quickPicks: picks };
  }
  return out;
}

function readJson(key: string): unknown {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // filters are a convenience; the screen still works without them
  }
}
